import React, { useState, useEffect, useMemo } from 'react';
import { usePermify } from '@permify/react-role';
// React and hooks
import { useHistory } from 'react-router-dom';
import {
  Button,
  Col,
  Collapse,
  Row,
  Space,
  Typography,
  Card,
  Form,
  Select,
  message,
  Dropdown,
  Divider,
  Input,
  InputNumber,
  DatePicker,
  Spin,
  Alert,
  Tooltip,
  Table,
  Tag,
  Progress,
  Switch,
  Modal,
} from 'antd';
import {
  LoadingOutlined,
  ClockCircleOutlined,
  RocketOutlined,
  InfoCircleOutlined,
  SaveOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

// Shared components
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { validateCampaignOrder, validateCampaignForLaunch, validateAllDatesForLaunch } from '../../CampaignRequestor/CampaignRequestorUtil';
import { detectFlightOverlaps } from '../../CampaignRequestor/utils/campaignValidation';
import { useMutation } from 'react-apollo';
import UPDATE_CAMPAIGN_STATUS from '../../../../core/GraphQl/Mutations/UPDATE_CAMPAIGN_STATUS';
import UPDATE_LINE_ITEM_DSP_SETTINGS from '../../../../core/GraphQl/Mutations/UPDATE_LINE_ITEM_DSP_SETTINGS';
import useCampaignLaunchProgress from '../../../../core/hooks/useCampaignLaunchProgress';
import useAutoAdjustDates from '../hooks/useAutoAdjustDates';

// Local components
import { CampaignOrderQueueReviewCampaign } from './CampaignOrderQueueReviewCampaign';
import CampaignLaunchProgressModal from './CampaignLaunchProgressModal';
import { LaunchSummaryPanel } from './LaunchSummaryPanel';
import DateValidationError from './DateValidationError';

// Styles
import '../../CampaignRequestor/styles.css';
import './styles.css';
import { getCampaignOrderErrorMessage } from '../../../../core/utils/constants/campaignOrderErrorType';
import { useCampaignOrderQueueContext } from '../../../../core/components/campaigns/campaignordersqueue/useCampaignOrderQueueContext';
import moment from 'moment-timezone';
import { formatCampaignName } from '../../../../core/utils/campaignNaming';
import { useTimezone } from '../../../../core/hooks/useTimezone';
import { formatCurrency2SigFig } from '../../../../core/utils/campaigns';
const { Panel } = Collapse;
const { Title, Text } = Typography;

// ESLint falsely reports hooks as "called conditionally" due to complex control flow.
// All hooks are actually at the component's top level - this is a known ESLint limitation.
// TODO: Investigate refactoring to satisfy the linter without this disable.
import sanitizePayload from '../../../../core/utils/sanitizePayload';

/* eslint-disable react-hooks/rules-of-hooks */
export const CampaignOrderQueueCampaignReview = ({ form, fieldName, campaignIndex, onMediaStatusUpdate, refetchCampaignOrder, campaignOrderData }) => {
  const { timezone } = useTimezone();
  const { updateCampaignOrder, currentCampaignOrder, loadingUpdateCampaignOrder, selectedAdvertiser } =
    useCampaignRequestorContext();
  const { launchCampaignOrderProgrammatic, loadingLaunchCampaingOrderProgrammatic, updateManyMedias } =
    useCampaignOrderQueueContext();
  const [updateCampaignStatus, { loading: loadingUpdateCampaignStatus }] = useMutation(UPDATE_CAMPAIGN_STATUS);
  const [updateLineItemDspSettings] = useMutation(UPDATE_LINE_ITEM_DSP_SETTINGS);
  const [loadingApproveAll, setLoadingApproveAll] = useState(false);

  // Wrapped mutations that sanitize payloads to prevent DOM-node / circular-ref crashes
  const safeUpdateCampaignOrder = async (options) => {
    const sanitized = {
      ...options,
      variables: sanitizePayload(options.variables),
    };
    return updateCampaignOrder(sanitized);
  };

  const safeUpdateCampaignStatus = async (options) => {
    const sanitized = {
      ...options,
      variables: sanitizePayload(options.variables),
    };
    return updateCampaignStatus(sanitized);
  };

  const safeUpdateLineItemDspSettings = async (options) => {
    const sanitized = {
      ...options,
      variables: sanitizePayload(options.variables),
    };
    return updateLineItemDspSettings(sanitized);
  };

  const safeLaunchCampaignOrderProgrammatic = async (options) => {
    const sanitized = {
      ...options,
      variables: sanitizePayload(options.variables),
    };
    return launchCampaignOrderProgrammatic(sanitized);
  };

  const campaign = form.getFieldValue('campaigns')?.[campaignIndex];
  const currentCampaign = currentCampaignOrder?.campaigns?.[campaignIndex];
  const campaignId = currentCampaign?.id;
  const campaignStatus = currentCampaign?.status;

  // Check if any OTHER campaign in this campaign order is currently launching PROGRAMMATICALLY
  // Manual launches can happen in parallel, only programmatic launches need to be sequential
  const getOtherProgrammaticLaunchingCampaign = () => {
    return currentCampaignOrder?.campaigns?.find((camp, index) => {
      if (index === campaignIndex) return false; // Skip current campaign
      const isLaunchingProgrammatically =
        ['LAUNCHING_LIVE', 'LAUNCHING_PAUSED'].includes(camp.status) &&
        camp.isLaunchProgrammatically === true;
      return isLaunchingProgrammatically;
    });
  };

  const otherProgrammaticLaunchingCampaign = getOtherProgrammaticLaunchingCampaign();
  const isAnotherProgrammaticCampaignLaunching = !!otherProgrammaticLaunchingCampaign;

  // Check if Ad Provider ID is pre-populated (which prevents programmatic launch)
  const hasAdProviderId = !!(campaign?.adProviderId && campaign.adProviderId.trim() !== '');

  // Detect UPDATE mode: campaign already launched with adProviderId
  const isUpdateMode = hasAdProviderId;

  // Count new line items (marked for delivery but not yet launched to DSP)
  const newLineItems = useMemo(() => {
    if (!currentCampaign?.lineItems) return [];
    return currentCampaign.lineItems.filter(
      (lineItem) => lineItem.markedForDelivery && !lineItem.dspLineItemId
    );
  }, [currentCampaign.lineItems]);

  const newLineItemCount = newLineItems.length;

  // Count modified line items (those with PENDING status that need updating)
  const modifiedLineItems = useMemo(() => {
    if (!currentCampaign?.lineItems) return [];
    return currentCampaign.lineItems.filter(
      (lineItem) => lineItem.dspLineItemId && (lineItem.status === 'PLAY_PENDING' || lineItem.status === 'PAUSED_PENDING')
    );
  }, [currentCampaign.lineItems]);

  const modifiedLineItemCount = modifiedLineItems.length;

  // Helper function to detect line items with new creatives
  const getNewCreativesInfo = () => {
    if (!currentCampaign?.lineItems) return { items: [], count: 0, total: 0 };
    const items = [];
    currentCampaign.lineItems.forEach((li) => {
      if (!li.markedForDelivery || !li.dspLineItemId) return;
      const newMedias = [];
      (li.adGroup?.adGroupCreatives || []).forEach((c) => {
        (c.medias || []).forEach((m) => {
          if (!m.dspCreativeId) newMedias.push({ creativeName: c.name, mediaName: m.name });
        });
      });
      if (newMedias.length > 0) items.push({ ...li, newMediaCount: newMedias.length, newMedias });
    });
    return { items, count: items.length, total: items.reduce((s, i) => s + i.newMediaCount, 0) };
  };

  // Cache the new creatives info
  const newCreativesInfo = getNewCreativesInfo();

  // Smart blocking: only block when hasAdProviderId AND no new items AND no modified items
  // For launched campaigns (hasAdProviderId), always allow updates - IO-level changes (budget, dates, maxBid)
  // can be synced even without new line items. For new campaigns, require line items to launch.
  const hasNewItemsToLaunch = hasAdProviderId || newLineItemCount > 0 || modifiedLineItemCount > 0;

  // Detect UPDATE mode for programmatic campaigns:
  // Campaign already launched programmatically AND has status LIVE/PAUSED (including _APPROVED states)
  // Always allow updates regardless of new/modified line items (for IO-level changes like budget, dates, maxBid)
  // NOTE: When campaignOrder status is PAUSED_PENDING, hide buttons while pause update is pending
  const isProgrammaticUpdateMode =
    currentCampaign?.isLaunchProgrammatically === true &&
    ['LIVE', 'LIVE_PENDING', 'LIVE_APPROVED', 'PAUSED', 'PAUSED_APPROVED'].includes(campaignStatus) &&
    currentCampaignOrder?.status !== 'PAUSED_PENDING';

  // Check if campaign is already launched (LIVE or PAUSED including PENDING states) for button label changes
  const isCampaignLaunched = ['LIVE', 'LIVE_PENDING', 'LIVE_APPROVED', 'PAUSED', 'PAUSED_PENDING', 'PAUSED_APPROVED'].includes(campaignStatus);

  // Track campaign launch progress using campaign ID
  const isLaunching = ['LAUNCHING_LIVE', 'LAUNCHING_PAUSED'].includes(campaignStatus);

  const progressData = useCampaignLaunchProgress(currentCampaignOrder?.id, {
    enabled: isLaunching,
    autoCleanup: false,
    onCompleted: async (progress) => {
      if (refetchCampaignOrder) {
        await refetchCampaignOrder();
      }
    },
    onFailed: async (progress) => {
      if (refetchCampaignOrder) {
        await refetchCampaignOrder();
      }
    }
  });

  // Launch Summary - displays results from programmatic campaign launches
  // launchSummary is keyed by campaignId in dspConfig.launchSummary
  const launchSummary = currentCampaignOrder?.dspConfig?.launchSummary?.[campaignId];
  const showLaunchSummary = launchSummary &&
    !isLaunching &&
    ['LIVE', 'PAUSED', 'LIVE_PENDING', 'PAUSED_PENDING', 'LIVE_APPROVED', 'PAUSED_APPROVED'].includes(campaignStatus);

  const { isAuthorized } = usePermify();
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [demandSidePlatform, setDemandSidePlatform] = useState(
    campaign ? campaign.demandSidePlatform : null
  );
  const [isManualLaunch, setIsManualLaunch] = useState(false);
  const [dateValidationErrors, setDateValidationErrors] = useState([]);
  const [showAddLineItemsModal, setShowAddLineItemsModal] = useState(false);
  const [pendingLaunchStatus, setPendingLaunchStatus] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentLaunchStatus, setCurrentLaunchStatus] = useState(null);
  const [showOnlyDeliveryItems, setShowOnlyDeliveryItems] = useState(false);
  const [isUpdatingAdProviderId, setIsUpdatingAdProviderId] = useState(false);
  const [isUpdatingMaxBid, setIsUpdatingMaxBid] = useState(false);
  const [isCancellingLaunch, setIsCancellingLaunch] = useState(false);
  const [isResettingToFresh, setIsResettingToFresh] = useState(false);

  // Flight schedule state for campaign-level flights
  const [flightSchedule, setFlightSchedule] = useState(currentCampaign?.flightSchedules || []);
  const [hasInitializedFlightSchedule, setHasInitializedFlightSchedule] = useState(false);
  const [isSavingCampaign, setIsSavingCampaign] = useState(false);

  const history = useHistory();

  // Advanced Mode Detection Logic
  useEffect(() => {
    const checkAdvancedMode = async () => {
      // Check if user has required permissions (isTrafficker OR isProcessor)
      const hasPermission = await isAuthorized(['isTrafficker', 'isProcessor'], []);

      // Check if campaign status is NOT DRAFT
      const campaignStatus = currentCampaignOrder?.status;
      const isNotDraft = campaignStatus && campaignStatus !== 'DRAFT';

      // Check if at least one campaign has hasMediaPlanStrategy enabled
      // const campaigns = currentCampaignOrder?.campaigns || [];
      // const hasStrategy = campaigns.some(camp => camp.hasMediaPlanStrategy === true);

      // Advanced Mode is available when ALL conditions are met
      const canAccessAdvancedMode = hasPermission && isNotDraft;
      setIsAdvancedMode(canAccessAdvancedMode);
    };

    checkAdvancedMode();
  }, [isAuthorized, currentCampaignOrder]);

  // Initialize flight schedule from existing campaign data ONCE only
  useEffect(() => {
    const existingFlights = currentCampaign?.flightSchedules || [];
    if (existingFlights.length > 0 && !hasInitializedFlightSchedule) {
      setFlightSchedule(existingFlights);
      setHasInitializedFlightSchedule(true);
    }
  }, [currentCampaign.flightSchedules, hasInitializedFlightSchedule]);

  // Sync flight schedule state to form values whenever it changes
  useEffect(() => {
    const campaigns = form.getFieldValue('campaigns') || [];
    const currentFormFlightSchedule = campaigns[campaignIndex]?.flightSchedule || [];

    // Simple length check + JSON comparison to detect changes
    const hasChanged = JSON.stringify(currentFormFlightSchedule) !== JSON.stringify(flightSchedule);

    if (hasChanged && flightSchedule.length > 0) {
      const updatedCampaigns = campaigns.map((c, idx) => {
        if (idx === campaignIndex) {
          return {
            ...c,
            flightSchedule: flightSchedule,
          };
        }
        return c;
      });

      form.setFieldsValue({ campaigns: updatedCampaigns });
    }
  }, [flightSchedule, form, campaignIndex]);

  /**
   * Auto-adjust campaign end date when flight schedule end date exceeds it
   */
  const autoAdjustEndDatesFromFlightSchedule = (updatedFlightSchedule) => {
    if (!updatedFlightSchedule || updatedFlightSchedule.length === 0) return;

    // Find the furthest end date among all flight schedules
    let furthestEndDate = null;
    updatedFlightSchedule.forEach((flight) => {
      if (flight.endDate) {
        const flightEnd = moment(flight.endDate);
        if (!furthestEndDate || flightEnd.isAfter(furthestEndDate)) {
          furthestEndDate = flightEnd;
        }
      }
    });

    if (!furthestEndDate) return;

    const fullFormValues = form.getFieldsValue();
    const fullFormUpdates = {};

    // Check if campaign order end date needs adjustment
    const currentOrderEnd = fullFormValues.endDate ? moment(fullFormValues.endDate) : null;
    let needsOrderUpdate = false;
    if (!currentOrderEnd || furthestEndDate.isAfter(currentOrderEnd)) {
      needsOrderUpdate = true;
      fullFormUpdates.endDate = furthestEndDate;
    }

    // Check if campaign end date needs adjustment
    const allCampaigns = fullFormValues.campaigns || [];
    const currentCampaignData = allCampaigns[campaignIndex];
    const currentCampaignEnd = currentCampaignData?.endDate ? moment(currentCampaignData.endDate) : null;
    let needsCampaignUpdate = false;
    if (!currentCampaignEnd || furthestEndDate.isAfter(currentCampaignEnd)) {
      needsCampaignUpdate = true;
      const updatedAllCampaigns = allCampaigns.map((c, cIdx) => {
        if (cIdx === campaignIndex) {
          return { ...c, endDate: furthestEndDate };
        }
        return c;
      });
      fullFormUpdates.campaigns = updatedAllCampaigns;
    }

    if (Object.keys(fullFormUpdates).length > 0) {
      form.setFieldsValue(fullFormUpdates);

      const adjustedItems = [];
      if (needsCampaignUpdate) adjustedItems.push('campaign');
      if (needsOrderUpdate) adjustedItems.push('campaign order');

      message.info(
        `End date auto-adjusted for ${adjustedItems.join(', ')} to match flight schedule end date (${furthestEndDate.tz(timezone).format('MM/DD/YYYY')})`,
        4
      );
    }
  };

  /**
   * Save campaign-level settings (excludes line items)
   * Handles DSP settings, flight schedules, and other campaign-level fields
   */
  const handleSaveCampaign = async () => {
    // Validate flight schedule before saving (if any flights exist)
    if (flightSchedule.length > 0) {
      const overlaps = detectFlightOverlaps(flightSchedule);
      if (overlaps.length > 0) {
        message.error('Cannot save: Flight schedules have overlapping date ranges');
        return;
      }

      // Check for missing required fields in flights
      const hasInvalidFlights = flightSchedule.some(
        (flight) => !flight.startDate || !flight.endDate || !flight.budget || flight.budget <= 0
      );
      if (hasInvalidFlights) {
        message.error('Cannot save: All flights must have start date, end date, and budget');
        return;
      }
    }

    setIsSavingCampaign(true);

    try {
      // Get form values for campaign-level fields
      const formCampaign = form.getFieldValue('campaigns')[campaignIndex];

      // Helper functions for date normalization
      const normalizeStartDate = (date) => {
        if (!date) return null;
        return moment(date).tz(timezone).startOf('day').utc().toISOString();
      };

      const normalizeEndDate = (date) => {
        if (!date) return null;
        return moment(date).tz(timezone).endOf('day').utc().toISOString();
      };

      // Build campaign update payload (excluding line items)
      const campaignUpdateData = {
        demandSidePlatform: formCampaign.demandSidePlatform || null,
        adProviderId: formCampaign.adProviderId || null,
        maxBid: formCampaign.maxBid ? Number(formCampaign.maxBid) : null,
      };

      // Build flight schedules payload
      const originalFlights = currentCampaign?.flightSchedules || [];

      if (flightSchedule.length > 0) {
        // Separate new flights (without IDs) from existing flights (with IDs)
        const newFlights = flightSchedule.filter((flight) => !flight.id);
        const existingFlights = flightSchedule.filter((flight) => flight.id);

        const flightSchedulesPayload = {};

        // Create new flights
        if (newFlights.length > 0) {
          flightSchedulesPayload.create = newFlights.map((flight) => ({
            startDate: normalizeStartDate(flight.startDate),
            endDate: normalizeEndDate(flight.endDate),
            budget: Number(flight.budget) || 0,
            cpm: Number(flight.cpm) || 7,
          }));
        }

        // Delete removed flights
        const currentFlightIds = new Set(existingFlights.map((f) => f.id));
        const flightsToDelete = originalFlights.filter((orig) => !currentFlightIds.has(orig.id));
        if (flightsToDelete.length > 0) {
          flightSchedulesPayload.delete = flightsToDelete.map((f) => ({ id: f.id }));
        }

        // Update modified flights
        const flightsToUpdate = [];
        existingFlights.forEach((currentFlight) => {
          const originalFlight = originalFlights.find((orig) => orig.id === currentFlight.id);
          if (originalFlight) {
            const hasChanged =
              normalizeStartDate(currentFlight.startDate) !== normalizeStartDate(originalFlight.startDate) ||
              normalizeEndDate(currentFlight.endDate) !== normalizeEndDate(originalFlight.endDate) ||
              Number(currentFlight.budget) !== Number(originalFlight.budget) ||
              Number(currentFlight.cpm) !== Number(originalFlight.cpm);

            if (hasChanged) {
              flightsToUpdate.push({
                where: { id: currentFlight.id },
                data: {
                  startDate: normalizeStartDate(currentFlight.startDate),
                  endDate: normalizeEndDate(currentFlight.endDate),
                  budget: Number(currentFlight.budget) || 0,
                  cpm: Number(currentFlight.cpm) || 7,
                },
              });
            }
          }
        });

        if (flightsToUpdate.length > 0) {
          flightSchedulesPayload.update = flightsToUpdate;
        }

        if (Object.keys(flightSchedulesPayload).length > 0) {
          campaignUpdateData.flightSchedules = flightSchedulesPayload;
        }
      } else if (originalFlights.length > 0) {
        // All flight schedules removed — delete them all
        campaignUpdateData.flightSchedules = {
          delete: originalFlights.map((f) => ({ id: f.id })),
        };
      }

      // Save using updateCampaignStatus mutation
      await safeUpdateCampaignStatus({
        variables: {
          where: { id: campaignId },
          data: campaignUpdateData,
        },
      });

      message.success('Campaign settings saved successfully');

      // Refresh campaign order data
      if (refetchCampaignOrder) {
        await refetchCampaignOrder();
      }
    } catch (error) {
      console.error('Save campaign error:', error);
      message.error(getCampaignOrderErrorMessage(error));
    } finally {
      setIsSavingCampaign(false);
    }
  };

  // Determine pricing model for display
  const pricingModel = useMemo(() => {
    // Priority 1: Use pricingModel directly from campaignOrder (string value)
    let model = currentCampaignOrder?.pricingModel;

    // Priority 2: Try selectedAdvertiser.pricingModel.model
    if (!model && selectedAdvertiser?.pricingModel?.model) {
      model = selectedAdvertiser.pricingModel.model;
    }

    // Priority 3: Try currentCampaignOrder.advertiser.pricingModel.model
    if (!model && currentCampaignOrder?.advertiser?.pricingModel?.model) {
      model = currentCampaignOrder.advertiser.pricingModel.model;
    }

    if (!model) {
      return null;
    }

    if (model === 'Percentage') {
      return {
        name: 'Percentage',
        description: 'Budget-based pricing',
        color: 'purple',
        tooltip: 'This campaign uses percentage-based pricing where costs are calculated as a percentage of the budget allocation.'
      };
    }

    if (model === 'CPM') {
      return {
        name: 'CPM',
        description: 'Impression-based pricing',
        color: 'blue',
        tooltip: 'This campaign uses CPM (Cost Per Mille) pricing where costs are based on impressions delivered.'
      };
    }

    return null;
  }, [selectedAdvertiser, currentCampaignOrder]);

  // Calculate pricing value for display
  const pricingValue = useMemo(() => {
    if (!pricingModel) return null;

    const campaign = currentCampaignOrder?.campaigns?.[campaignIndex];
    if (!campaign) return null;

    if (pricingModel.name === 'CPM') {
      // Get CPM value from campaign
      const cpmValue = campaign.cpm;
      return cpmValue ? formatCurrency2SigFig(cpmValue) : null;
    }

    if (pricingModel.name === 'Percentage') {
      // Get percentage value from campaign
      const percentageValue = campaign.advertiserPercentage;
      return percentageValue ? `${percentageValue}%` : null;
    }

    return null;
  }, [pricingModel, currentCampaignOrder, campaignIndex]);

  // Check if in review mode (for Approve All button visibility)
  const isReviewMode = useMemo(() => {
    const reviewStatuses = ['PENDING', 'REJECTED', 'LIVE_PENDING', 'PAUSED_PENDING'];
    return reviewStatuses.includes(currentCampaignOrder?.status);
  }, [currentCampaignOrder.status]);

  // Use campaignOrderData (from parent's local state) for UI updates, fall back to context
  const campaignDataForUI = campaignOrderData || currentCampaignOrder;
  const currentCampaignForUI = campaignDataForUI?.campaigns?.[campaignIndex];

  // Count pending creatives across all line items (display and video)
  const pendingCreativesInfo = useMemo(() => {
    const allPendingMediaIds = [];
    const updatesByLocation = []; // { lineItemIndex, adGroupCreativeIndex, mediaIds }

    currentCampaignForUI?.lineItems?.forEach((lineItem, lineItemIndex) => {
      // Check display creatives (adGroupCreatives)
      lineItem?.adGroup?.adGroupCreatives?.forEach((creative, creativeIndex) => {
        const pendingMedias = creative.medias?.filter(m => m.status !== 'APPROVED') || [];
        if (pendingMedias.length > 0) {
          allPendingMediaIds.push(...pendingMedias.map(m => m.id));
          updatesByLocation.push({
            lineItemIndex,
            adGroupCreativeIndex: creativeIndex,
            mediaIds: pendingMedias.map(m => m.id),
            type: 'display'
          });
        }
      });
    });

    return {
      totalPending: allPendingMediaIds.length,
      allMediaIds: allPendingMediaIds,
      updatesByLocation
    };
  }, [currentCampaignForUI]);

  // Handle bulk approval of all pending creatives
  const handleApproveAllCreatives = async () => {
    const { allMediaIds, updatesByLocation, totalPending } = pendingCreativesInfo;

    if (totalPending === 0) {
      message.info('All creatives are already approved');
      return;
    }

    setLoadingApproveAll(true);

    try {
      // Notify parent for each location (for UI updates)
      updatesByLocation.forEach(({ lineItemIndex, adGroupCreativeIndex, mediaIds }) => {
        if (onMediaStatusUpdate) {
          const updates = mediaIds.map(id => ({ id, status: 'APPROVED' }));
          onMediaStatusUpdate(campaignIndex, lineItemIndex, adGroupCreativeIndex, updates);
        }
      });

      // Single API call for all medias
      await updateManyMedias({
        variables: {
          data: { status: 'APPROVED' },
          where: { id_in: allMediaIds }
        },
        context: {
          skipRefetch: true
        }
      });

      message.success(`${totalPending} creative${totalPending > 1 ? 's' : ''} approved`);
    } catch (error) {
      message.error('Failed to approve creatives');
      console.error('Approve all creatives error:', error);
    } finally {
      setLoadingApproveAll(false);
    }
  };

  const save = async (status, options = {}) => {
    const { updateCampaignStatus = false, isManualLaunch = false, skipValidation = false } = options;

    // Pre-flight campaign order validation (skip if already validated in caller)
    if (!skipValidation) {
      if (isManualLaunch) {
        // For manual launch, only validate the specific campaign
        if (!validateCampaignForLaunch(currentCampaignOrder, campaignIndex, message)) {
          return;
        }
      } else {
        // For other operations, validate entire campaign order
        if (!validateCampaignOrder(currentCampaignOrder, message)) {
          return;
        }
      }
    } else {
    }

    // Validate form fields
    let values;
    try {
      if (skipValidation && isManualLaunch) {
        // For manual launch with skipValidation, only validate the specific campaign's fields
        const fieldsToValidate = [
          ['campaigns', campaignIndex, 'demandSidePlatform'],
          ['campaigns', campaignIndex, 'adProviderId'],
          ['campaigns', campaignIndex, 'maxBid'],
        ];
        await form.validateFields(fieldsToValidate);

        // Get all form values (we need the whole campaigns array for the mutation)
        values = form.getFieldsValue();
      } else {
        // For other operations, validate all fields
        values = await form.validateFields();
      }
    } catch (error) {
      message.error(error.errorFields[0].errors[0]);
      return;
    }

    try {


      // For manual launch, only include the campaign being launched in the mutation
      const campaignsToUpdate = (skipValidation && isManualLaunch)
        ? [values.campaigns[campaignIndex]]  // Only the clicked campaign
        : values.campaigns;  // All campaigns for other operations


      await safeUpdateCampaignOrder({
        variables: {
          where: { id: currentCampaignOrder.id },
          data: {
            status,
            campaigns: {
              update: campaignsToUpdate.map((campaign, index) => ({
                where: { id: campaign.id },
                data: {
                  demandSidePlatform: campaign.demandSidePlatform,
                  adProviderId: campaign.adProviderId || null,
                  maxBid: Number(campaign.maxBid || 0),

                  lineItems: {
                    update: campaign.lineItems.map((lineItem) => ({
                    where: { id: lineItem.id },
                    data: {
                      supplySidePlatform: lineItem.supplySidePlatform,
                      maxBid: Number(lineItem.maxBid || 0),
                      frequencyCap: Number(lineItem.frequencyCap || 0),
                      beacons: {
                        create: (lineItem.beacons || [])
                          .filter((beacon, index, array) =>
                            array.findIndex(b => b.beaconId === beacon.beaconId) === index
                          )
                          .map((beacon) => ({
                            beaconId: beacon.beaconId,
                            name: beacon.name,
                            advertiserId: beacon.advertiserId,
                            beaconType: beacon.beaconType,
                          })),
                      },
                      dealIds: {
                        create: lineItem.dealIds.map((data) => ({
                          dealId: data.dealId,
                          name: data.name,
                          org: {
                            connect: {
                              id: currentCampaignOrder.advertiser.id,
                            },
                          },
                        })),
                      },
                    },
                  })),
                },
                },
              })),
            },
          },
        },
      });
    } catch (error) {
      message.destroy();
      message.error(getCampaignOrderErrorMessage(error));
    }
  };

  const handleLaunchManual = async () => {

    setIsManualLaunch(true);

    // Pre-flight validation for this specific campaign only
    const validationResult = validateCampaignForLaunch(currentCampaignOrder, campaignIndex, message);

    if (!validationResult) {
      setIsManualLaunch(false);
      return;
    }

    // Validate tracking tags / awareness campaign. Any combination of the
    // three KPIs (transactions, sign-ups, sessions) is valid, as long as each
    // configured KPI actually has its tag.
    const hasTrackingTag = currentCampaignOrder.transactionsConfig?.tag || currentCampaignOrder.signUpConfig?.appId || currentCampaignOrder.sessionConfig?.appId;
    if (!currentCampaignOrder.awarenessCampaign && !hasTrackingTag) {
      setIsManualLaunch(false);
      message.error('Tracking tag required. Mark as Awareness-only or add a tracking tag before launching.');
      return;
    }
    if (currentCampaignOrder.transactionsConfig && !currentCampaignOrder.transactionsConfig.tag) {
      setIsManualLaunch(false);
      message.error('Transactions KPI is missing a tracking tag — select one or remove the KPI before launching.');
      return;
    }
    if (currentCampaignOrder.signUpConfig && !currentCampaignOrder.signUpConfig.appId) {
      setIsManualLaunch(false);
      message.error('Sign-Ups KPI is missing a tracking tag — select one or remove the KPI before launching.');
      return;
    }

    try {
      // Only validate fields for this specific campaign
      const fieldsToValidate = [
        ['campaigns', campaignIndex, 'demandSidePlatform'],
        ['campaigns', campaignIndex, 'adProviderId'],
        ['campaigns', campaignIndex, 'maxBid'],
      ];

      // Add validation for DSP Line Item IDs for line items marked for delivery
      if (isUpdateMode && currentCampaign?.lineItems) {
        currentCampaign.lineItems.forEach((lineItem, lineItemIndex) => {
          if (lineItem.markedForDelivery && !lineItem.dspLineItemId) {
            fieldsToValidate.push(['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'dspLineItemId']);
          }
        });
      }

      await form.validateFields(fieldsToValidate);
    } catch (error) {
      setIsManualLaunch(false);

      // Check if there are missing DSP Line Item IDs
      const hasMissingDspIds = isUpdateMode && currentCampaign?.lineItems.some(
        (lineItem) => lineItem.markedForDelivery && !lineItem.dspLineItemId
      );

      if (hasMissingDspIds) {
        message.warning('Please enter DSP Line Item IDs for all line items marked for delivery.');
      } else {
        message.warning('Please fix the validation errors before launching.');
      }
      return;
    }

    // Validate dates for manual launch (only end dates >= start dates)
    const dateValidation = validateAllDatesForLaunch(currentCampaignOrder, campaignIndex, 'manual');
    if (!dateValidation.isValid) {
      setDateValidationErrors(dateValidation.errors);
      message.error('Date validation failed. Please fix date issues before launching.');
      return;
    }

    try {
      message.loading('Launching campaign...', 0);

      // Get form values for the clicked campaign
      const values = form.getFieldsValue();
      const campaignData = values.campaigns[campaignIndex];
      const campaignId = currentCampaignOrder.campaigns[campaignIndex].id;

      // Preserve programmatic launch history: only set to false if not already programmatic
      const wasLaunchedProgrammatically = currentCampaign?.isLaunchProgrammatically === true;

      // Preserve current status if updating, otherwise set to LIVE for new launches
      const currentStatus = currentCampaign?.status;
      const targetStatus = isCampaignLaunched ? currentStatus : 'LIVE';

      // Step 1: Update the campaign status
      await safeUpdateCampaignStatus({
        variables: {
          where: { id: campaignId },
          data: {
            status: targetStatus,
            demandSidePlatform: campaignData.demandSidePlatform,
            adProviderId: campaignData.adProviderId || null,
            maxBid: Number(campaignData.maxBid || 0),
            isLaunchProgrammatically: wasLaunchedProgrammatically ? true : false,
            lineItems: {
              update: campaignData.lineItems.map((lineItem) => ({
                where: { id: lineItem.id },
                data: {
                  supplySidePlatform: lineItem.supplySidePlatform,
                  maxBid: Number(lineItem.maxBid || 0),
                  frequencyCap: Number(lineItem.frequencyCap || 0),
                  dspLineItemId: lineItem.dspLineItemId || null,
                  beacons: {
                    create: (lineItem.beacons || [])
                      .filter((beacon, index, array) =>
                        array.findIndex(b => b.beaconId === beacon.beaconId) === index
                      )
                      .map((beacon) => ({
                        beaconId: beacon.beaconId,
                        name: beacon.name,
                        advertiserId: beacon.advertiserId,
                        beaconType: beacon.beaconType,
                      })),
                  },
                  dealIds: {
                    create: lineItem.dealIds.map((data) => ({
                      dealId: data.dealId,
                      name: data.name,
                      org: {
                        connect: {
                          id: currentCampaignOrder.advertiser.id,
                        },
                      },
                    })),
                  },
                },
              }))
            }
          },
        },
      });

      // Step 2: Update the campaign order status
      if (!isCampaignLaunched) {
        // New launch: Set to LIVE
        await safeUpdateCampaignOrder({
          variables: {
            where: { id: currentCampaignOrder.id },
            data: {
              status: 'LIVE',
            },
          },
        });
      } else if (currentCampaignOrder.status === 'LIVE_APPROVED' || currentCampaignOrder.status === 'PAUSED_APPROVED') {
        // Update: Transition LIVE_APPROVED → LIVE or PAUSED_APPROVED → PAUSED
        const newStatus = currentCampaignOrder.status === 'LIVE_APPROVED' ? 'LIVE' : 'PAUSED';
        await safeUpdateCampaignOrder({
          variables: {
            where: { id: currentCampaignOrder.id },
            data: {
              status: newStatus,
            },
          },
        });
      }

      message.destroy();
      message.success('Campaign launched successfully');

      // Step 3: Refresh campaign order data to show updated status
      if (refetchCampaignOrder) {
        await refetchCampaignOrder();
      }

    } catch (error) {
      message.destroy();
      message.error(getCampaignOrderErrorMessage(error));
    } finally {
      setIsManualLaunch(false);
    }
  };

  const validateProgrammaticLaunch = async (options = {}) => {
    const { skipDateValidation = false } = options;
    const validationResult = validateCampaignForLaunch(currentCampaignOrder, campaignIndex, message);

    if (!validationResult) {
      return {
        isValid: false,
        shouldShowDateModal: false,
        validationError: 'Pre-flight validation failed'
      };
    }

    try {

      const campaignFieldsToValidate = [
        ['campaigns', campaignIndex, 'demandSidePlatform'],
        ['campaigns', campaignIndex, 'adProviderId'],
        ['campaigns', campaignIndex, 'maxBid'],
      ];

      const campaign = form.getFieldValue('campaigns')[campaignIndex];
      const lineItemFieldsToValidate = [];
      if (campaign && campaign.lineItems) {
        // Only validate line items that are marked for delivery
        campaign.lineItems.forEach((lineItem, lineItemIndex) => {
          if (lineItem.markedForDelivery) {
            lineItemFieldsToValidate.push(
              ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'supplySidePlatform'],
              ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'maxBid'],
              ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'frequencyCap']
            );
          }
        });
      }

      const allFieldsToValidate = [...campaignFieldsToValidate, ...lineItemFieldsToValidate];

      await form.validateFields(allFieldsToValidate);
    } catch (error) {
      message.warning('Please fix the validation errors before launching.');
      return {
        isValid: false,
        shouldShowDateModal: false,
        validationError: 'Form validation failed'
      };
    }

    // Validate tracking tags / awareness campaign. Any combination of the
    // three KPIs (transactions, sign-ups, sessions) is valid, as long as each
    // configured KPI actually has its tag.
    const hasTrackingTag = currentCampaignOrder.transactionsConfig?.tag || currentCampaignOrder.signUpConfig?.appId || currentCampaignOrder.sessionConfig?.appId;
    if (!currentCampaignOrder.awarenessCampaign && !hasTrackingTag) {
      message.error('Tracking tag required. Mark as Awareness-only or add a tracking tag before launching.');
      return {
        isValid: false,
        shouldShowDateModal: false,
        validationError: 'Tracking tag required',
        dateErrors: [],
        dateWarnings: []
      };
    }
    if (currentCampaignOrder.transactionsConfig && !currentCampaignOrder.transactionsConfig.tag) {
      message.error('Transactions KPI is missing a tracking tag — select one or remove the KPI before launching.');
      return {
        isValid: false,
        shouldShowDateModal: false,
        validationError: 'Transactions tag missing',
        dateErrors: [],
        dateWarnings: []
      };
    }
    if (currentCampaignOrder.signUpConfig && !currentCampaignOrder.signUpConfig.appId) {
      message.error('Sign-Ups KPI is missing a tracking tag — select one or remove the KPI before launching.');
      return {
        isValid: false,
        shouldShowDateModal: false,
        validationError: 'Sign-Ups tag missing',
        dateErrors: [],
        dateWarnings: []
      };
    }

    // Only Nexxen segment SETS can be attached at the DSP — a plain 'segment'
    // (legacy row from the old segments dialog) fails the launch deep in the
    // external service. Block NEW launches with an actionable message; for
    // updates the pre-launch save purges legacy segments automatically.
    if (!isUpdateMode) {
      const segmentCheckCampaign = form.getFieldValue('campaigns')[campaignIndex];
      const segmentCheckOriginals = currentCampaignOrder.campaigns[campaignIndex]?.lineItems || [];
      const offendingLineItem = (segmentCheckCampaign?.lineItems || []).find((lineItem, liIndex) => {
        if (!lineItem.markedForDelivery) return false;
        const segments = lineItem.segments || segmentCheckOriginals[liIndex]?.segments || [];
        return segments.some((s) => s.segmentType && s.segmentType !== 'segmentSet');
      });
      if (offendingLineItem) {
        message.error(
          `Line item "${offendingLineItem.name || 'Unnamed'}" has an unsupported Nexxen segment attached. Only segment sets can be launched — open the campaign in the requestor, remove the segment (green "Segment" tag), and attach a segment set instead.`
        );
        return {
          isValid: false,
          shouldShowDateModal: false,
          validationError: 'Unsupported Nexxen segment on line item',
          dateErrors: [],
          dateWarnings: []
        };
      }
    }

    // Validate dates for programmatic launch and update
    if (!skipDateValidation) {
      // For updates (already launched campaigns), skip start date validation
      // Only new launches need to validate start dates are not in the past
      const dateValidationType = isUpdateMode ? 'manual' : 'programmatic';
      const dateValidationResult = validateAllDatesForLaunch(
        currentCampaignOrder,
        campaignIndex,
        dateValidationType
      );

      if (!dateValidationResult.isValid) {
        // For UPDATES (already-live campaigns), date issues are advisory, not blocking.
        // The campaign is already running, and a creative/budget update must not be
        // forced to fix pre-existing date state (e.g., a flight ending a day before a
        // line item) — saveAllDspSettingsBeforeLaunch already auto-extends the last
        // flight to cover line items, so blocking here would prevent the very update
        // that resolves it. Surface as warnings so the update can proceed; only NEW
        // launches are blocked until dates are fixed.
        if (isUpdateMode) {
          return {
            isValid: true,
            shouldShowDateModal: false,
            validationError: null,
            dateErrors: [],
            dateWarnings: dateValidationResult.errors,
          };
        }
        // For new launches, block until dates are fixed.
        return {
          isValid: false,
          shouldShowDateModal: false,
          validationError: 'Date validation failed',
          dateErrors: dateValidationResult.errors,
          dateWarnings: dateValidationResult.errors
        };
      }
    }

    // Check for flight schedule overlaps
    const flightSchedules = currentCampaign?.flightSchedules || [];
    if (flightSchedules.length > 1) {
      const overlaps = detectFlightOverlaps(flightSchedules);
      if (overlaps.length > 0) {
        const overlapErrors = overlaps.map(overlap => ({
          level: 'CAMPAIGN',
          entityName: `Campaign "${currentCampaign.name || `Campaign ${campaignIndex + 1}`}"`,
          message: `Flight ${overlap.flightIndex1 + 1} and Flight ${overlap.flightIndex2 + 1} have overlapping date ranges`
        }));
        return {
          isValid: false,
          shouldShowDateModal: false,
          validationError: 'Flight schedule overlap detected',
          dateErrors: overlapErrors
        };
      }
    }

    return {
      isValid: true,
      shouldShowDateModal: false,
      validationError: null,
      dateErrors: []
    };
  };

  const { handleAutoAdjustDates, isAutoAdjusting: isAutoAdjustingDates } = useAutoAdjustDates({
    currentCampaignOrder,
    campaignIndex,
    updateCampaignStatus: safeUpdateCampaignStatus,
    updateLineItemDspSettings: safeUpdateLineItemDspSettings,
    refetchCampaignOrder,
    onComplete: () => setDateValidationErrors([]),
  });

  /**
   * Handle confirmation of adding new line items
   */
  const handleConfirmAddLineItems = async () => {
    setShowAddLineItemsModal(false);

    if (!pendingLaunchStatus) {
      message.error('No launch status specified');
      return;
    }

    // Proceed with validation
    const validation = await validateProgrammaticLaunch();

    if (!validation.isValid) {
      // If there are date errors, set state to display them
      if (validation.dateErrors && validation.dateErrors.length > 0) {
        setDateValidationErrors(validation.dateErrors);
      }
      return;
    }

    // Show date warnings without blocking launch
    if (validation.dateWarnings && validation.dateWarnings.length > 0) {
      setDateValidationErrors(validation.dateWarnings);
    }

    // Continue with the programmatic launch process
    await executeProgrammaticLaunch(pendingLaunchStatus);
  };

  /**
   * Handle programmatic update for campaigns already launched.
   * Updates should keep the campaign live by default; pausing must be an explicit choice.
   */
  const handleUpdateProgrammatic = async () => {
    await handleLaunchProgrammatic('play');
  };

  /**
   * Save all DSP settings (campaign + line items) before programmatic launch
   * @returns {Promise<boolean>} - true if all saves succeeded, false otherwise
   */
  const saveAllDspSettingsBeforeLaunch = async () => {
    const values = form.getFieldsValue();
    const campaignData = values.campaigns[campaignIndex];
    const campaignId = currentCampaignOrder.campaigns[campaignIndex].id;

    // Helper functions for date normalization
    const normalizeStartDate = (date) => {
      if (!date) return null;
      return moment(date).tz(timezone).startOf('day').utc().toISOString();
    };

    const normalizeEndDate = (date) => {
      if (!date) return null;
      return moment(date).tz(timezone).endOf('day').utc().toISOString();
    };

    // Step 1: Save campaign-level DSP settings
    const campaignUpdateData = {
      demandSidePlatform: campaignData.demandSidePlatform || null,
      adProviderId: campaignData.adProviderId || null,
      maxBid: campaignData.maxBid ? Number(campaignData.maxBid) : null,
    };

    // Build campaign flight schedules payload
    const originalCampaignFlights = currentCampaign?.flightSchedules || [];
    const currentCampaignFlights = flightSchedule || [];

    // Auto-extend: ensure the last campaign flight covers all line items.
    // The DSP IO/Package schedule is derived from campaign-level flights.
    // If the last flight ends before the latest line item end date, the
    // campaign stops serving early. Extend the last flight to cover it.
    if (currentCampaignFlights.length > 0) {
      const allLineItemEnds = (campaignData.lineItems || [])
        .filter((li) => li.markedForDelivery !== false)
        .map((li) => li.endDate)
        .filter(Boolean)
        .map((d) => moment(d).tz(timezone).endOf('day'));

      const latestLineItemEnd = allLineItemEnds.reduce((latest, d) => {
        return (!latest || d.isAfter(latest)) ? d : latest;
      }, null);

      const lastFlight = currentCampaignFlights[currentCampaignFlights.length - 1];
      if (latestLineItemEnd && lastFlight.endDate) {
        const lastFlightEnd = moment(lastFlight.endDate).tz(timezone).endOf('day');
        if (lastFlightEnd.isBefore(latestLineItemEnd)) {
          lastFlight.endDate = latestLineItemEnd.utc().toISOString();
        }
      }
    }

    if (currentCampaignFlights.length > 0) {
      const newFlights = currentCampaignFlights.filter((flight) => !flight.id);
      const existingFlights = currentCampaignFlights.filter((flight) => flight.id);

      const flightSchedulesPayload = {};

      if (newFlights.length > 0) {
        flightSchedulesPayload.create = newFlights.map((flight) => ({
          startDate: normalizeStartDate(flight.startDate),
          endDate: normalizeEndDate(flight.endDate),
          budget: Number(flight.budget) || 0,
          cpm: Number(flight.cpm) || 7,
        }));
      }

      const currentFlightIds = new Set(existingFlights.map((f) => f.id));
      const flightsToDelete = originalCampaignFlights.filter((orig) => !currentFlightIds.has(orig.id));
      if (flightsToDelete.length > 0) {
        flightSchedulesPayload.delete = flightsToDelete.map((f) => ({ id: f.id }));
      }

      const flightsToUpdate = [];
      existingFlights.forEach((currentFlight) => {
        const originalFlight = originalCampaignFlights.find((orig) => orig.id === currentFlight.id);
        if (originalFlight) {
          const hasChanged =
            normalizeStartDate(currentFlight.startDate) !== normalizeStartDate(originalFlight.startDate) ||
            normalizeEndDate(currentFlight.endDate) !== normalizeEndDate(originalFlight.endDate) ||
            Number(currentFlight.budget) !== Number(originalFlight.budget) ||
            Number(currentFlight.cpm) !== Number(originalFlight.cpm);

          if (hasChanged) {
            flightsToUpdate.push({
              where: { id: currentFlight.id },
              data: {
                startDate: normalizeStartDate(currentFlight.startDate),
                endDate: normalizeEndDate(currentFlight.endDate),
                budget: Number(currentFlight.budget) || 0,
                cpm: Number(currentFlight.cpm) || 7,
              },
            });
          }
        }
      });

      if (flightsToUpdate.length > 0) {
        flightSchedulesPayload.update = flightsToUpdate;
      }

      if (Object.keys(flightSchedulesPayload).length > 0) {
        campaignUpdateData.flightSchedules = flightSchedulesPayload;
      }
    } else if (originalCampaignFlights.length > 0) {
      campaignUpdateData.flightSchedules = {
        delete: originalCampaignFlights.map((f) => ({ id: f.id })),
      };
    }

    // Save campaign settings
    await safeUpdateCampaignStatus({
      variables: {
        where: { id: campaignId },
        data: campaignUpdateData,
      },
    });

    // Step 2: Save all line items' DSP settings in parallel
    const lineItemsToSave = campaignData.lineItems || [];
    const originalLineItems = currentCampaign?.lineItems || [];

    const lineItemSavePromises = lineItemsToSave.map(async (lineItemForm, lineItemIndex) => {
      const originalLineItem = originalLineItems[lineItemIndex] || {};
      // Use ID from form values first, fall back to original line item
      const lineItemId = lineItemForm.id || originalLineItem.id;

      if (!lineItemId) {
        console.warn(`[saveAllDspSettings] Line item at index ${lineItemIndex} has no ID, skipping`);
        return;
      }

      // Build line item update data - use form values with fallback to context
      const lineItemUpdateData = {
        supplySidePlatform: lineItemForm.supplySidePlatform || originalLineItem.supplySidePlatform || null,
        maxBid: lineItemForm.maxBid ? parseFloat(lineItemForm.maxBid) : (originalLineItem.maxBid ? parseFloat(originalLineItem.maxBid) : null),
        frequencyCap: lineItemForm.frequencyCap ? parseInt(lineItemForm.frequencyCap, 10) : (originalLineItem.frequencyCap ? parseInt(originalLineItem.frequencyCap, 10) : null),
      };

      // Build flight schedules payload for line item - use form values with fallback to context
      // Note: form uses 'flightSchedule' (singular), context uses 'flightSchedules' (plural)
      const originalFlightSchedules = originalLineItem.flightSchedules || [];
      const currentFlightSchedules = lineItemForm.flightSchedule || lineItemForm.flightSchedules || originalLineItem.flightSchedules || [];

      if (currentFlightSchedules.length > 0) {
        const newFlights = currentFlightSchedules.filter(f => !f.id);
        const existingFlights = currentFlightSchedules.filter(f => f.id);

        const flightSchedulesPayload = {};

        if (newFlights.length > 0) {
          flightSchedulesPayload.create = newFlights.map(flight => ({
            startDate: normalizeStartDate(flight.startDate),
            endDate: normalizeEndDate(flight.endDate),
            budget: Number(flight.budget) || 0,
            ...(flight.cpm !== undefined ? { cpm: Number(flight.cpm) || 7 } : {}),
          }));
        }

        const currentFlightIds = new Set(existingFlights.map(f => f.id));
        const flightsToDelete = originalFlightSchedules.filter(orig => !currentFlightIds.has(orig.id));
        if (flightsToDelete.length > 0) {
          flightSchedulesPayload.delete = flightsToDelete.map(f => ({ id: f.id }));
        }

        // Only update flights that actually changed — mirrors campaign-flight diffing.
        // Previously every existing line-item flight was re-pushed on every update with
        // raw (un-normalized) dates, which risked schedule drift if a form date was stale
        // or a Moment object slipped through. Compare normalized dates + budget + cpm.
        const flightsToUpdate = [];
        existingFlights.forEach((currentFlight) => {
          const originalFlight = originalFlightSchedules.find((orig) => orig.id === currentFlight.id);
          if (!originalFlight) return;
          const hasChanged =
            normalizeStartDate(currentFlight.startDate) !== normalizeStartDate(originalFlight.startDate) ||
            normalizeEndDate(currentFlight.endDate) !== normalizeEndDate(originalFlight.endDate) ||
            Number(currentFlight.budget) !== Number(originalFlight.budget) ||
            (Number(currentFlight.cpm) || 0) !== (Number(originalFlight.cpm) || 0);
          if (hasChanged) {
            flightsToUpdate.push({
              where: { id: currentFlight.id },
              data: {
                startDate: normalizeStartDate(currentFlight.startDate),
                endDate: normalizeEndDate(currentFlight.endDate),
                budget: Number(currentFlight.budget) || 0,
                ...(currentFlight.cpm !== undefined ? { cpm: Number(currentFlight.cpm) || 7 } : {}),
              },
            });
          }
        });
        if (flightsToUpdate.length > 0) {
          flightSchedulesPayload.update = flightsToUpdate;
        }

        if (Object.keys(flightSchedulesPayload).length > 0) {
          lineItemUpdateData.flightSchedules = flightSchedulesPayload;
        }
      }

      // Build beacons payload - use form values with fallback to context
      const currentBeacons = lineItemForm.beacons || originalLineItem.beacons || [];
      const originalBeacons = originalLineItem.beacons || [];

      if (currentBeacons.length > 0 || originalBeacons.length > 0) {
        const newBeacons = currentBeacons.filter(b => !b.id);
        const existingBeacons = currentBeacons.filter(b => b.id);

        const beaconsPayload = {};

        if (newBeacons.length > 0) {
          beaconsPayload.create = newBeacons.map(beacon => ({
            beaconId: beacon.beaconId,
            name: beacon.name,
            beaconType: beacon.beaconType,
          }));
        }

        const currentBeaconIds = new Set(existingBeacons.map(b => b.id));
        const beaconsToDelete = originalBeacons.filter(orig => !currentBeaconIds.has(orig.id));
        if (beaconsToDelete.length > 0) {
          beaconsPayload.delete = beaconsToDelete.map(b => ({ id: b.id }));
        }

        if (Object.keys(beaconsPayload).length > 0) {
          lineItemUpdateData.beacons = beaconsPayload;
        }
      }

      // Build inventory sources payload - use form values with fallback to context
      const currentInventorySources = lineItemForm.inventorySources || originalLineItem.inventorySources || [];
      const originalInventorySources = originalLineItem.inventorySources || [];

      if (currentInventorySources.length > 0 || originalInventorySources.length > 0) {
        const newSources = currentInventorySources.filter(s => !s.id);
        const existingSources = currentInventorySources.filter(s => s.id);

        const inventorySourcesPayload = {};

        if (newSources.length > 0) {
          inventorySourcesPayload.create = newSources.map(source => ({
            sourceId: source.sourceId,
            name: source.name,
          }));
        }

        const currentSourceIds = new Set(existingSources.map(s => s.id));
        const sourcesToDelete = originalInventorySources.filter(orig => !currentSourceIds.has(orig.id));
        if (sourcesToDelete.length > 0) {
          inventorySourcesPayload.delete = sourcesToDelete.map(s => ({ id: s.id }));
        }

        if (Object.keys(inventorySourcesPayload).length > 0) {
          lineItemUpdateData.inventorySources = inventorySourcesPayload;
        }
      }

      // Build inventory lists payload - use form values with fallback to context
      const currentInventoryLists = lineItemForm.inventoryLists || originalLineItem.inventoryLists || [];
      const originalInventoryLists = originalLineItem.inventoryLists || [];

      if (currentInventoryLists.length > 0 || originalInventoryLists.length > 0) {
        const newLists = currentInventoryLists.filter(l => !l.id);
        const existingLists = currentInventoryLists.filter(l => l.id);

        const inventoryListsPayload = {};

        if (newLists.length > 0) {
          inventoryListsPayload.create = newLists.map(list => ({
            listId: list.listId,
            name: list.name,
            action: list.action || 'include',
          }));
        }

        const currentListIds = new Set(existingLists.map(l => l.id));
        const listsToDelete = originalInventoryLists.filter(orig => !currentListIds.has(orig.id));
        if (listsToDelete.length > 0) {
          inventoryListsPayload.delete = listsToDelete.map(l => ({ id: l.id }));
        }

        const listsToUpdate = existingLists
          .filter(l => {
            const original = originalInventoryLists.find(orig => orig.id === l.id);
            return original && original.action !== l.action;
          })
          .map(l => ({
            where: { id: l.id },
            data: { action: l.action },
          }));
        if (listsToUpdate.length > 0) {
          inventoryListsPayload.update = listsToUpdate;
        }

        if (Object.keys(inventoryListsPayload).length > 0) {
          lineItemUpdateData.inventoryLists = inventoryListsPayload;
        }
      }

      // Build DSP deal IDs payload - use form values with fallback to context
      const currentDspDealIds = lineItemForm.dspDealIds || originalLineItem.dspDealIds || [];
      const originalDspDealIds = originalLineItem.dspDealIds || [];

      if (currentDspDealIds.length > 0 || originalDspDealIds.length > 0) {
        const newDeals = currentDspDealIds.filter(d => !d.id);
        const existingDeals = currentDspDealIds.filter(d => d.id);

        const dspDealIdsPayload = {};

        if (newDeals.length > 0) {
          dspDealIdsPayload.create = newDeals.map(deal => ({
            dealId: deal.dealId,
            name: deal.name,
            cpmBid: deal.cpmBid || null,
          }));
        }

        const currentDealIds = new Set(existingDeals.map(d => d.id));
        const dealsToDelete = originalDspDealIds.filter(orig => !currentDealIds.has(orig.id));
        if (dealsToDelete.length > 0) {
          dspDealIdsPayload.delete = dealsToDelete.map(d => ({ id: d.id }));
        }

        if (Object.keys(dspDealIdsPayload).length > 0) {
          lineItemUpdateData.dspDealIds = dspDealIdsPayload;
        }
      }

      // Build segments payload - use form values with fallback to context.
      // Only segment SETS are supported by the Nexxen attach API — plain
      // 'segment' entries (legacy rows from the old segments dialog) are never
      // (re)created, and since they are excluded from currentSegmentIds, any
      // already stored on the line item are deleted here.
      const rawCurrentSegments = lineItemForm.segments || originalLineItem.segments || [];
      const currentSegments = rawCurrentSegments.filter((s) => s.segmentType === 'segmentSet');
      if (rawCurrentSegments.length > currentSegments.length) {
        console.warn(
          `[saveAllDspSettings] Dropping unsupported Nexxen segments on line item ${lineItemId} (only segment sets can be attached):`,
          rawCurrentSegments.filter((s) => s.segmentType !== 'segmentSet')
        );
      }
      const originalSegments = originalLineItem.segments || [];

      if (currentSegments.length > 0 || originalSegments.length > 0) {
        const newSegments = currentSegments.filter(s => !s.id);
        const existingSegments = currentSegments.filter(s => s.id);

        const segmentsPayload = {};

        if (newSegments.length > 0) {
          segmentsPayload.create = newSegments.map(segment => ({
            segmentId: segment.segmentId,
            name: segment.name,
            segmentType: segment.segmentType || 'segmentSet',
            cost: segment.cost || 0,
          }));
        }

        const currentSegmentIds = new Set(existingSegments.map(s => s.id));
        const segmentsToDelete = originalSegments.filter(orig => !currentSegmentIds.has(orig.id));
        if (segmentsToDelete.length > 0) {
          segmentsPayload.delete = segmentsToDelete.map(s => ({ id: s.id }));
        }

        if (Object.keys(segmentsPayload).length > 0) {
          lineItemUpdateData.segments = segmentsPayload;
        }
      }

      // Save line item DSP settings
      try {
        await safeUpdateLineItemDspSettings({
          variables: {
            lineItemId: lineItemId,
            data: lineItemUpdateData,
          },
        });
      } catch (lineItemError) {
        console.error(`[saveAllDspSettings] Error saving line item ${lineItemId}:`, lineItemError);
        throw lineItemError;
      }
    });

    // Wait for all line item saves to complete
    await Promise.all(lineItemSavePromises);

    return true;
  };

  /**
   * Execute the programmatic launch after all validations
   * @param {string} launchStatus - 'pause' or 'play'
   */
  const executeProgrammaticLaunch = async (launchStatus) => {
    try {
      // Store the launch status for completion handling
      setCurrentLaunchStatus(launchStatus);

      // Runtime guard: re-check for concurrent programmatic launches right before
      // executing to close the race window between UI render and click.
      const concurrentCampaign = getOtherProgrammaticLaunchingCampaign();
      if (concurrentCampaign) {
        throw new Error(
          `Sequential launch guard: "${concurrentCampaign.name || 'Another campaign'}" is currently launching. Please wait for it to complete.`
        );
      }

      // NOTE: Previously auto-set awareness campaign when tracking tags were missing.
      // This has been removed — launch is now blocked with a clear warning instead,
      // and the user must explicitly mark as awareness-only or add tracking tags.

      // Step -0.4: Clear stale dspConfig error from previous failed launch
      if (currentCampaignOrder.dspConfig?.error || currentCampaignOrder.dspConfig?.status === 'ERROR') {
        await safeUpdateCampaignOrder({
          variables: {
            where: { id: currentCampaignOrder.id },
            data: {
              dspConfig: {
                ...currentCampaignOrder.dspConfig,
                status: 'PENDING',
                error: null,
                lastErrorAt: null,
              },
            },
          },
        });
        message.info('Cleared previous launch error');
      }

      // NOTE: Previously auto-adjusted past dates during launch.
      // This has been removed — launch is now blocked with a clear warning instead,
      // and the user must explicitly adjust dates via the requestor or the
      // one-click date-adjust utility on the review page.

      // Step 0: Save all DSP settings (campaign + line items) before launching
      message.loading({ content: 'Saving all DSP settings...', key: 'saveDspSettings' });
      try {
        await saveAllDspSettingsBeforeLaunch();
        message.success({ content: 'DSP settings saved successfully', key: 'saveDspSettings', duration: 2 });
      } catch (dspSettingsError) {
        console.error('[executeProgrammaticLaunch] Error saving DSP settings:', dspSettingsError);
        message.error({ content: `Failed to save DSP settings: ${dspSettingsError.message}`, key: 'saveDspSettings' });
        throw dspSettingsError;
      }

      // Get form values for the clicked campaign
      const values = form.getFieldsValue();
      const campaignData = values.campaigns[campaignIndex];
      const campaignId = currentCampaignOrder.campaigns[campaignIndex].id;

      // Determine if this is UPDATE mode (campaign already has adProviderId).
      // IMPORTANT: read from the FORM (campaignData), NOT from currentCampaign (server state).
      // currentCampaign comes from currentCampaignOrder, which can be STALE right after the user
      // clears the Ad Provider ID — refetchCampaignOrder() may not have propagated yet. Reading the
      // stale value sent a just-cleared campaign down the UPDATE path against the old archived IO.
      // The form reflects the user's actual intent (cleared = ''), so it is authoritative here.
      const adProviderId = campaignData?.adProviderId;
      const isUpdateMode = !!(adProviderId && String(adProviderId).trim() !== '');

      // Step 1: Update the campaign status to APPROVED (will be updated to LIVE/PAUSED after external service completes)
      await safeUpdateCampaignStatus({
        variables: {
          where: { id: campaignId },
          data: {
            status: launchStatus === 'play' ? 'LAUNCHING_LIVE' : 'LAUNCHING_PAUSED',
            demandSidePlatform: campaignData.demandSidePlatform,
            // Only set adProviderId to null for INITIAL launch, preserve it for UPDATE mode
            adProviderId: isUpdateMode ? adProviderId : null,
            maxBid: Number(campaignData.maxBid || 0),
            isLaunchProgrammatically: true,  // Programmatic launch
            lineItems: {
              update: campaignData.lineItems.map((lineItem) => ({
                where: { id: lineItem.id },
                data: {
                  supplySidePlatform: lineItem.supplySidePlatform,
                  maxBid: Number(lineItem.maxBid || 0),
                  frequencyCap: Number(lineItem.frequencyCap || 0),
                  beacons: {
                    create: (lineItem.beacons || [])
                      .filter((beacon, index, array) =>
                        array.findIndex(b => b.beaconId === beacon.beaconId) === index
                      )
                      .map((beacon) => ({
                        beaconId: beacon.beaconId,
                        name: beacon.name,
                        advertiserId: beacon.advertiserId,
                        beaconType: beacon.beaconType,
                      })),
                  },
                  dealIds: {
                    create: lineItem.dealIds.map((data) => ({
                      dealId: data.dealId,
                      name: data.name,
                      org: {
                        connect: {
                          id: currentCampaignOrder.advertiser.id,
                        },
                      },
                    })),
                  },
                },
              }))
            }
          },
        },
      });

      // Status update will be handled on the backend
      // await save(currentCampaignOrder.status);

      await safeLaunchCampaignOrderProgrammatic({
        variables: {
          data: {
            id: currentCampaignOrder.id,
            launchStatus, // pass "play" or "pause"
            alternativeName: formatCampaignName({
              ...currentCampaignOrder,
              name: currentCampaignOrder.campaigns[campaignIndex].name,
            }, timezone),
            campaignId,
          },
        },
      });

      // Update campaign order status for APPROVED variants
      if (currentCampaignOrder.status === 'LIVE_APPROVED' || currentCampaignOrder.status === 'PAUSED_APPROVED') {
        const newStatus = currentCampaignOrder.status === 'LIVE_APPROVED' ? 'LIVE' : 'PAUSED';
        await safeUpdateCampaignOrder({
          variables: {
            where: { id: currentCampaignOrder.id },
            data: {
              status: newStatus,
            },
          },
        });
      }

      if (isUpdateMode) {
        message.success(
          `Syncing campaign to DSP with status: ${launchStatus === 'play' ? 'LIVE' : 'PAUSED'}`
        );
      } else {
        message.success(
          `Campaign launch initiated with status: ${launchStatus === 'play' ? 'LIVE' : 'PAUSED'}`
        );
      }

      // Small delay to ensure mutation has propagated through database
      await new Promise(resolve => setTimeout(resolve, 500));

      if (refetchCampaignOrder) {
        await refetchCampaignOrder();
      } else {
        console.warn('[CampaignReview] refetchCampaignOrder is not available!');
      }
    } catch (error) {
      console.error('❌ Error during programmatic launch:', error);
      message.error(getCampaignOrderErrorMessage(error));
    }
  };

  /**
   * Cancel/Reset a stuck launch - reverts campaign and campaign order status
   * Logic:
   * - If campaign has adProviderId (already launched before):
   *   - LAUNCHING_PAUSED -> PAUSED_APPROVED
   *   - LAUNCHING_LIVE -> LIVE_APPROVED
   * - If campaign has no adProviderId (never launched):
   *   - LAUNCHING_PAUSED -> APPROVED
   *   - LAUNCHING_LIVE -> APPROVED
   */
  const handleCancelLaunch = async () => {
    try {
      setIsCancellingLaunch(true);

      const campaignData = currentCampaignOrder.campaigns[campaignIndex];
      const hasExistingAdProviderId = !!(campaignData?.adProviderId && campaignData.adProviderId.trim() !== '');
      const isLaunchingLive = campaignStatus === 'LAUNCHING_LIVE';

      // Determine new campaign status
      let newCampaignStatus;
      if (hasExistingAdProviderId) {
        newCampaignStatus = isLaunchingLive ? 'LIVE_APPROVED' : 'PAUSED_APPROVED';
      } else {
        newCampaignStatus = 'APPROVED';
      }

      // Update campaign status
      await safeUpdateCampaignStatus({
        variables: {
          where: { id: campaignId },
          data: { status: newCampaignStatus },
        },
      });

      // Determine and update campaign order status
      let newCampaignOrderStatus;
      if (hasExistingAdProviderId) {
        newCampaignOrderStatus = isLaunchingLive ? 'LIVE_APPROVED' : 'PAUSED_APPROVED';
      } else {
        newCampaignOrderStatus = 'APPROVED';
      }

      await safeUpdateCampaignOrder({
        variables: {
          where: { id: currentCampaignOrder.id },
          data: { status: newCampaignOrderStatus },
        },
      });

      message.success('Launch cancelled. Status has been reset.');

      // Refetch to update UI
      if (refetchCampaignOrder) {
        await refetchCampaignOrder();
      }
    } catch (error) {
      console.error('Error cancelling launch:', error);
      message.error('Failed to cancel launch. Please try again.');
    } finally {
      setIsCancellingLaunch(false);
    }
  };

  /**
   * Full reset to fresh launch.
   * Clears all DSP references (adProviderId, dspPackageIds, per-line-item dspLineItemId) AND
   * resets campaign + campaign-order status to APPROVED. Use this when a launch failed or the
   * campaign is stuck in a LIVE/update state and you need a clean re-launch that CREATES a new
   * Insertion Order instead of updating the existing one.
   * NOTE: orphaned DSP entities from prior failed launches are NOT removed by this action.
   */
  const handleResetToFreshLaunch = () => {
    const campaignForReset = currentCampaignOrder?.campaigns?.[campaignIndex];
    const currentAdProviderId = campaignForReset?.adProviderId || '';
    const currentDspPackageIds = campaignForReset?.dspPackageIds || [];
    const lineItemsWithDspId = (campaignForReset?.lineItems || []).filter(
      (lineItem) => lineItem.dspLineItemId
    );

    Modal.confirm({
      title: 'Reset to fresh launch',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <p>
            This fully resets the campaign so the next launch creates a brand-new Insertion Order
            in the DSP instead of updating the existing one.
          </p>
          {currentAdProviderId && (
            <p><strong>Ad Provider ID to clear:</strong> {currentAdProviderId}</p>
          )}
          {currentDspPackageIds.length > 0 && (
            <p><strong>DSP Package IDs to clear:</strong> {currentDspPackageIds.join(', ')}</p>
          )}
          {lineItemsWithDspId.length > 0 && (
            <p><strong>DSP Line Item IDs to clear:</strong> {lineItemsWithDspId.length} line item(s)</p>
          )}
          <p><strong>Campaign &amp; order status &rarr; APPROVED</strong></p>
          <p style={{ color: '#ff4d4f', marginTop: 12 }}>
            Warning: This clears all DSP references and resets status to APPROVED. Orphaned entities
            in the DSP from prior failed launches are not removed. Use only when you need a clean re-launch.
          </p>
        </div>
      ),
      okText: 'Reset to fresh launch',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        setIsResettingToFresh(true);
        try {
          // 1. Clear DSP IDs + reset campaign status to APPROVED
          await safeUpdateCampaignStatus({
            variables: {
              where: { id: campaignId },
              data: {
                status: 'APPROVED',
                adProviderId: null,
                dspPackageIds: { set: [] },
              },
            },
          });

          // 2. Clear DSP Line Item IDs on all line items that have them
          if (lineItemsWithDspId.length > 0) {
            await Promise.all(
              lineItemsWithDspId.map((lineItem) =>
                safeUpdateLineItemDspSettings({
                  variables: {
                    lineItemId: lineItem.id,
                    data: { dspLineItemId: null },
                  },
                })
              )
            );
          }

          // 3. Reset campaign order status to APPROVED
          await safeUpdateCampaignOrder({
            variables: {
              where: { id: currentCampaignOrder.id },
              data: { status: 'APPROVED' },
            },
          });

          // 4. Update local form state to reflect the reset
          form.setFieldsValue({
            campaigns: form
              .getFieldValue('campaigns')
              .map((c, idx) =>
                idx === campaignIndex
                  ? {
                      ...c,
                      adProviderId: '',
                      dspPackageIds: [],
                      lineItems: (c.lineItems || []).map((li) => ({
                        ...li,
                        dspLineItemId: null,
                      })),
                    }
                  : c
              ),
          });

          message.success('Campaign reset to fresh launch. Next launch will create a new IO.');

          if (refetchCampaignOrder) {
            await refetchCampaignOrder();
          }
        } catch (error) {
          console.error('Error resetting campaign to fresh launch:', error);
          message.error(getCampaignOrderErrorMessage(error));
        } finally {
          setIsResettingToFresh(false);
        }
      },
    });
  };

  /**
   * Launch campaign programmatically
   * @param {string} launchStatus - 'pause' or 'play'
   */
  const handleLaunchProgrammatic = async (launchStatus = 'pause') => {
    setIsManualLaunch(false);

    // Check if Ad Provider ID is already provided - allow IO-only updates (budget, dates, maxBid)
    const campaignData = form.getFieldValue('campaigns')[campaignIndex];
    if (campaignData?.adProviderId && campaignData.adProviderId.trim() !== '') {
      // Has adProviderId - show modal for update confirmation
      // IO-level changes (budget, dates, maxBid) can be synced even without new line items
      setPendingLaunchStatus(launchStatus);
      setShowAddLineItemsModal(true);
      return;
    }

    const validation = await validateProgrammaticLaunch();

    if (!validation.isValid) {
      // If there are date errors, set state to display them
      if (validation.dateErrors && validation.dateErrors.length > 0) {
        setDateValidationErrors(validation.dateErrors);
      }
      return;
    }

    // Show date warnings without blocking launch
    if (validation.dateWarnings && validation.dateWarnings.length > 0) {
      setDateValidationErrors(validation.dateWarnings);
    }

    // Validation passed, execute launch
    await executeProgrammaticLaunch(launchStatus);
  };

  const handleUpdateAdProviderId = () => {
    const currentAdProviderId = currentCampaignOrder.campaigns[campaignIndex]?.adProviderId || '';
    const newAdProviderId = form.getFieldValue(['campaigns', campaignIndex, 'adProviderId']) || '';

    if (currentAdProviderId === newAdProviderId) {
      message.info('No changes to save. The Ad Provider ID value is unchanged.');
      return;
    }

    if (!newAdProviderId || newAdProviderId.trim() === '') {
      message.warning('Ad Provider ID cannot be empty. Use the delete button to remove it.');
      return;
    }

    Modal.confirm({
      title: 'Update Ad Provider ID',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to update the Ad Provider ID?</p>
          <p><strong>Current value:</strong> {currentAdProviderId || '(empty)'}</p>
          <p><strong>New value:</strong> {newAdProviderId}</p>
          <p style={{ color: '#faad14', marginTop: 12 }}>
            This change will take effect immediately.
          </p>
        </div>
      ),
      okText: 'Update',
      cancelText: 'Cancel',
      onOk: async () => {
        setIsUpdatingAdProviderId(true);
        try {
          await safeUpdateCampaignStatus({
            variables: {
              where: { id: campaignId },
              data: {
                adProviderId: newAdProviderId.trim(),
              },
            },
          });
          message.success('Ad Provider ID updated successfully');
          if (refetchCampaignOrder) {
            await refetchCampaignOrder();
          }
        } catch (error) {
          message.error(getCampaignOrderErrorMessage(error));
        } finally {
          setIsUpdatingAdProviderId(false);
        }
      },
    });
  };

  const handleRemoveAdProviderId = () => {
    const currentAdProviderId = currentCampaignOrder.campaigns[campaignIndex]?.adProviderId || '';
    const currentDspPackageIds = currentCampaignOrder.campaigns[campaignIndex]?.dspPackageIds || [];
    const lineItemsWithDspId = (currentCampaignOrder.campaigns[campaignIndex]?.lineItems || []).filter(
      (li) => li.dspLineItemId
    );

    if (!currentAdProviderId || currentAdProviderId.trim() === '') {
      message.info('Ad Provider ID is already empty.');
      return;
    }

    Modal.confirm({
      title: 'Remove Ad Provider ID',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <p>Are you sure you want to remove the Ad Provider ID?</p>
          <p><strong>Current Ad Provider ID:</strong> {currentAdProviderId}</p>
          {currentDspPackageIds.length > 0 && (
            <p><strong>DSP Package IDs to clear:</strong> {currentDspPackageIds.join(', ')}</p>
          )}
          {lineItemsWithDspId.length > 0 && (
            <p><strong>DSP Line Item IDs to clear:</strong> {lineItemsWithDspId.length} line item(s)</p>
          )}
          <p style={{ color: '#ff4d4f', marginTop: 12 }}>
            Warning: This will reset the campaign to launch mode. The next launch will create a new Insertion Order in the DSP instead of updating the existing one.
          </p>
        </div>
      ),
      okText: 'Remove',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        setIsUpdatingAdProviderId(true);
        try {
          // Clear Ad Provider ID and DSP Package IDs from campaign
          await safeUpdateCampaignStatus({
            variables: {
              where: { id: campaignId },
              data: {
                adProviderId: null,
                dspPackageIds: { set: [] },
              },
            },
          });

          // Clear DSP Line Item IDs from all line items
          if (lineItemsWithDspId.length > 0) {
            await Promise.all(
              lineItemsWithDspId.map((lineItem) =>
                safeUpdateLineItemDspSettings({
                  variables: {
                    lineItemId: lineItem.id,
                    data: { dspLineItemId: null },
                  },
                })
              )
            );
          }

          // Update form state
          form.setFieldsValue({
            campaigns: form
              .getFieldValue('campaigns')
              .map((c, idx) =>
                idx === campaignIndex
                  ? {
                      ...c,
                      adProviderId: '',
                      dspPackageIds: [],
                      lineItems: (c.lineItems || []).map((li) => ({ ...li, dspLineItemId: null })),
                    }
                  : c
              ),
          });
          message.success('Ad Provider ID, DSP Package IDs, and DSP Line Item IDs removed successfully');
          if (refetchCampaignOrder) {
            await refetchCampaignOrder();
          }
        } catch (error) {
          message.error(getCampaignOrderErrorMessage(error));
        } finally {
          setIsUpdatingAdProviderId(false);
        }
      },
    });
  };

  const handleUpdateMaxBid = () => {
    const currentMaxBid = currentCampaignOrder.campaigns[campaignIndex]?.maxBid || 0;
    const newMaxBid = form.getFieldValue(['campaigns', campaignIndex, 'maxBid']);

    if (currentMaxBid === newMaxBid) {
      message.info('No changes to save. The Maximum Bid value is unchanged.');
      return;
    }

    if (!newMaxBid || newMaxBid <= 0) {
      message.warning('Maximum Bid must be greater than zero.');
      return;
    }

    Modal.confirm({
      title: 'Update Maximum Bid',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to update the Maximum Bid?</p>
          <p><strong>Current value:</strong> ${currentMaxBid?.toFixed(2) || '0.00'}</p>
          <p><strong>New value:</strong> ${Number(newMaxBid).toFixed(2)}</p>
          <p style={{ color: '#faad14', marginTop: 12 }}>
            This change will take effect immediately.
          </p>
        </div>
      ),
      okText: 'Update',
      cancelText: 'Cancel',
      onOk: async () => {
        setIsUpdatingMaxBid(true);
        try {
          await safeUpdateCampaignStatus({
            variables: {
              where: { id: campaignId },
              data: {
                maxBid: Number(newMaxBid),
              },
            },
          });
          message.success('Maximum Bid updated successfully');
          if (refetchCampaignOrder) {
            await refetchCampaignOrder();
          }
        } catch (error) {
          message.error(getCampaignOrderErrorMessage(error));
        } finally {
          setIsUpdatingMaxBid(false);
        }
      },
    });
  };

  return (
    <Spin spinning={loadingLaunchCampaingOrderProgrammatic || loadingUpdateCampaignOrder || loadingUpdateCampaignStatus || isUpdatingAdProviderId || loadingApproveAll}>
      <div id={`campaign-${campaignIndex}`}>
        <Collapse bordered={false} defaultActiveKey={[0]}>
          <Panel
            open={true}
            header={
              <Space>
                <Title level={5} style={{ margin: 0 }}>Campaign</Title>
                {/* Pricing Model Indicator */}
                {pricingModel && (
                  <Tooltip title={pricingModel.tooltip}>
                    <Tag color={pricingModel.color}>
                      {pricingModel.name}: {pricingModel.description}
                    </Tag>
                  </Tooltip>
                )}
              </Space>
            }
          >
          <Card>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* Show alert when another programmatic campaign is launching */}
              {isAnotherProgrammaticCampaignLaunching &&
               !isLaunching && (
                <Alert
                  message="Sequential Launch in Progress"
                  description={
                    <span>
                      Campaign "<strong>{otherProgrammaticLaunchingCampaign?.name || 'Another campaign'}</strong>" is currently launching programmatically.
                      Please wait for it to complete before launching this campaign programmatically. Manual launches are still available.
                    </span>
                  }
                  type="info"
                  showIcon
                  icon={<LoadingOutlined />}
                  closable={false}
                />
              )}
              {/* DSP Fields - Only visible in Advanced Mode */}
              {isAdvancedMode && ['APPROVED', 'LIVE_APPROVED', 'PAUSED_APPROVED', 'LIVE', 'PAUSED'].includes(currentCampaignOrder?.status) && (
                <>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text>DSP</Text>
                        <Text type="secondary">
                          <small> Demand-Side Platform </small>
                        </Text>
                        <Form.Item
                          name={[fieldName, 'demandSidePlatform']}
                          rules={[
                            {
                              required: true,
                              message: 'Please select a DSP',
                            },
                          ]}
                        >
                          <Select
                            style={{ width: '100%' }}
                            showSearch
                            optionFilterProp="children"
                            onChange={(value) => setDemandSidePlatform(value)}
                            filterOption={(input, option) =>
                              option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            options={[
                              { value: 'NEXXEN', label: 'NEXXEN' },
                              { value: 'LIQUIDM', label: 'LIQUIDM' },
                              { value: 'JAMLOOP', label: 'JAMLOOP' },
                            ]}
                            disabled={isLaunching}
                          />
                        </Form.Item>
                      </Space>
                    </Col>
                    <Col span={6}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Space size={8} align="center">
                          <Text>Ad Provider ID</Text>
                          <Tooltip title="Save Ad Provider ID">
                            <Button
                              type="text"
                              size="small"
                              icon={<SaveOutlined />}
                              onClick={handleUpdateAdProviderId}
                              disabled={isLaunching || isUpdatingAdProviderId}
                              style={{ padding: '0 4px' }}
                            />
                          </Tooltip>
                          <Tooltip title="Remove Ad Provider ID">
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={handleRemoveAdProviderId}
                              disabled={isLaunching || isUpdatingAdProviderId || !currentCampaignOrder.campaigns[campaignIndex]?.adProviderId}
                              style={{ padding: '0 4px' }}
                            />
                          </Tooltip>
                        </Space>
                        <Text type="secondary">
                          <small> Unique identifier for the ad provider </small>
                        </Text>
                        {hasAdProviderId && (
                          <div>
                            <Button
                              danger
                              size="small"
                              icon={<ReloadOutlined />}
                              loading={isResettingToFresh}
                              onClick={handleResetToFreshLaunch}
                              style={{ marginTop: 4 }}
                            >
                              Reset to fresh launch
                            </Button>
                            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                              Clears DSP IDs &amp; resets status for a clean re-launch
                            </Text>
                          </div>
                        )}
                        <Form.Item
                          name={[fieldName, 'adProviderId']}
                          rules={[
                            {
                              validator: (_, value) => {
                                if (isManualLaunch && (!value || value.trim() === '')) {
                                  return Promise.reject(
                                    'Ad Provider ID is required for manual launch'
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            style={{ width: '100%' }}
                            onChange={(e) =>
                              form.setFieldsValue({
                                campaigns: form
                                  .getFieldValue('campaigns')
                                  .map((c, idx) =>
                                    idx === campaignIndex
                                      ? { ...c, adProviderId: e.target.value }
                                      : c
                                  ),
                              })
                            }
                            value={(campaign && campaign.adProviderId) || ''}
                            disabled={isLaunching || isUpdatingAdProviderId}
                          />
                        </Form.Item>
                      </Space>
                    </Col>
                    {/* Only show Maximum Bid if pricing model is not Percentage */}
                    {pricingModel?.name !== 'Percentage' && (
                      <Col span={7}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Space size={8} align="center">
                            <Text>Maximum Bid</Text>
                            <Tooltip title="Save Maximum Bid">
                              <Button
                                type="text"
                                size="small"
                                icon={<SaveOutlined />}
                                onClick={handleUpdateMaxBid}
                                disabled={isLaunching || isUpdatingMaxBid}
                                style={{ padding: '0 4px' }}
                              />
                            </Tooltip>
                          </Space>
                          <Text type="secondary">
                            <small> Maximum bid amount for the campaign </small>
                          </Text>
                          <Form.Item
                            name={[fieldName, 'maxBid']}
                            rules={[
                              {
                                required: true,
                                message: 'Maximum bid is required',
                              },
                              {
                                type: 'number',
                                min: 0.01,
                                message: 'Maximum bid must be greater than zero',
                              },
                            ]}
                          >
                            <Input
                              type="number"
                              step="0.01"
                              style={{ width: '100%' }}
                              onChange={(e) => {
                                const value = e.target.value;
                                const numericValue = value === '' ? null : parseFloat(value);
                                form.setFieldsValue({
                                  campaigns: form
                                    .getFieldValue('campaigns')
                                    .map((c, idx) =>
                                      idx === campaignIndex
                                        ? { ...c, maxBid: numericValue }
                                        : c
                                    ),
                                });
                              }}
                              value={(campaign && campaign.maxBid) || 4.00}
                              disabled={isLaunching || isUpdatingMaxBid}
                            />
                          </Form.Item>
                        </Space>
                      </Col>
                    )}
                  </Row>
                </>
              )}

              {/* Campaign Details Section */}
              <Row gutter={20}>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Campaign Type</Text>
                    <Text>
                      {(() => {
                        const typeLabels = {
                          // Numeric types
                          1: 'Display',
                          7: 'External',
                          8: 'Video',
                          // String types
                          'DISPLAY': 'Display',
                          'EXTERNAL': 'External',
                          'VIDEO': 'Video',
                        };
                        const campaignType = currentCampaignOrder.campaigns[campaignIndex]?.type;
                        return typeLabels[campaignType] || 'Display';
                      })()}
                    </Text>
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Campaign Name</Text>
                    <Text>
                      {currentCampaignOrder.campaigns[campaignIndex]?.name || `Campaign ${campaignIndex + 1}`}
                    </Text>
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Budget</Text>
                    <Text>
                      {formatCurrency2SigFig(currentCampaignOrder.campaigns[campaignIndex]?.budgetTotal || 0)}
                    </Text>
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Date Range</Text>
                    <Text>
                      {currentCampaignOrder.campaigns[campaignIndex]?.startDate &&
                       currentCampaignOrder.campaigns[campaignIndex]?.endDate
                        ? `${moment(currentCampaignOrder.campaigns[campaignIndex].startDate).tz(timezone).format('MM/DD/YYYY')} - ${moment(currentCampaignOrder.campaigns[campaignIndex].endDate).tz(timezone).format('MM/DD/YYYY')}`
                        : 'Not specified'}
                    </Text>
                  </Space>
                </Col>
              </Row>
              <Row gutter={20} style={{ marginTop: 16 }}>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Status</Text>
                    {(() => {
                      const status = currentCampaignOrder.campaigns[campaignIndex]?.status;
                      const statusConfig = {
                        PENDING: { color: 'default', text: 'Pending' },
                        APPROVED: { color: 'blue', text: 'Approved' },
                        LAUNCHING: { color: 'processing', text: 'Launching' },
                        LIVE: { color: 'success', text: 'Live' },
                        PAUSED: { color: 'warning', text: 'Paused' },
                        COMPLETED: { color: 'default', text: 'Completed' },
                        ARCHIVED: { color: 'default', text: 'Archived' },
                        ERROR: { color: 'error', text: 'Error' },
                      };
                      const config = statusConfig[status] || { color: 'default', text: status || 'Not Set' };
                      return <Tag color={config.color}>{config.text}</Tag>;
                    })()}
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Launch Type</Text>
                    {(() => {
                      const status = currentCampaignOrder.campaigns[campaignIndex]?.status;
                      const isLaunchProgrammatically = currentCampaignOrder.campaigns[campaignIndex]?.isLaunchProgrammatically;

                      if (!['LIVE', 'PAUSED'].includes(status)) {
                        return <Text type="secondary">-</Text>;
                      }

                      if (isLaunchProgrammatically) {
                        return <Tag color="purple">Programmatic</Tag>;
                      }
                      return <Tag color="blue">Manual</Tag>;
                    })()}
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Pricing Model</Text>
                    {pricingModel ? (
                      <Space size={4}>
                        <Tag color={pricingModel.color}>{pricingModel.name}</Tag>
                        {pricingValue && <Text strong>{pricingValue}</Text>}
                      </Space>
                    ) : (
                      <Text type="secondary">-</Text>
                    )}
                  </Space>
                </Col>
              </Row>

              {/* Campaign-Level Flight Schedules - Editable in Advanced Mode */}
              {isAdvancedMode && ['APPROVED', 'LIVE_APPROVED', 'PAUSED_APPROVED', 'LIVE', 'PAUSED'].includes(currentCampaignOrder?.status) && (
                <Row gutter={20} style={{ marginTop: 16 }}>
                  <Col span={24}>
                    <Card title="CAMPAIGN FLIGHT SCHEDULE" size="small">
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {/* Flight Overlap Warning */}
                        {(() => {
                          const overlaps = detectFlightOverlaps(flightSchedule);
                          if (overlaps.length === 0) return null;
                          return (
                            <Alert
                              type="error"
                              message="Overlapping Flight Dates Detected"
                              description={
                                <div>
                                  <p>The following flights have overlapping date ranges:</p>
                                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                                    {overlaps.map((overlap, idx) => (
                                      <li key={idx}>
                                        Flight {overlap.flightIndex1 + 1} and Flight {overlap.flightIndex2 + 1}
                                      </li>
                                    ))}
                                  </ul>
                                  <p style={{ marginTop: 8, marginBottom: 0 }}>
                                    Please adjust the date ranges to ensure flights do not overlap.
                                  </p>
                                </div>
                              }
                              showIcon
                            />
                          );
                        })()}
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <div>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                              Configure multiple flight date ranges for phased campaign execution (maps to Nexxen IO schedules)
                            </Text>
                          </div>
                          <Space>
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              disabled={isLaunching || isSavingCampaign}
                              onClick={() => {
                                let newStartDate, newEndDate;
                                const campaignData = currentCampaignOrder.campaigns[campaignIndex];

                                if (flightSchedule.length === 0) {
                                  // First flight: use campaign dates
                                  newStartDate = campaignData?.startDate
                                    ? moment(campaignData.startDate)
                                    : moment.tz(timezone).startOf('day').utc();
                                  newEndDate = campaignData?.endDate
                                    ? moment(campaignData.endDate)
                                    : moment.tz(timezone).add(2, 'days').endOf('day').utc();
                                } else {
                                  // Subsequent flights: start after the furthest end date
                                  let furthestEndDate = null;
                                  flightSchedule.forEach((flight) => {
                                    if (flight.endDate) {
                                      const flightEnd = moment(flight.endDate);
                                      if (!furthestEndDate || flightEnd.isAfter(furthestEndDate)) {
                                        furthestEndDate = flightEnd;
                                      }
                                    }
                                  });

                                  if (!furthestEndDate) {
                                    furthestEndDate = campaignData?.endDate ? moment(campaignData.endDate) : moment.tz(timezone);
                                  }

                                  // Convert to user timezone to get the calendar date, then add 1 day, convert back to UTC
                                  const nextDay = moment(furthestEndDate).tz(timezone).add(1, 'day').format('YYYY-MM-DD');
                                  newStartDate = moment.tz(nextDay, timezone).startOf('day').utc();
                                  newEndDate = moment.tz(nextDay, timezone).add(2, 'days').endOf('day').utc();
                                }

                                const isPercentage = pricingModel?.name === 'Percentage';
                                const newFlight = {
                                  startDate: newStartDate,
                                  endDate: newEndDate,
                                  budget: null,
                                };

                                // Only add CPM for CPM pricing model
                                if (!isPercentage) {
                                  newFlight.cpm = campaignData?.cpm || 7;
                                }

                                const updatedFlightSchedule = [...flightSchedule, newFlight];
                                setFlightSchedule(updatedFlightSchedule);

                                // Auto-adjust end dates if the new flight's end date exceeds them
                                autoAdjustEndDatesFromFlightSchedule(updatedFlightSchedule);
                              }}
                            >
                              Add Flight
                            </Button>
                          </Space>
                        </Space>

                        <Table
                          dataSource={flightSchedule.map((flight, idx) => {
                            const numberOfDays = flight.startDate && flight.endDate
                              ? moment(flight.endDate).diff(moment(flight.startDate), 'days') + 1
                              : null;
                            const impressionGoal = flight.budget && flight.cpm
                              ? Math.round((flight.budget / flight.cpm) * 1000)
                              : null;
                            return {
                              key: idx,
                              ...flight,
                              numberOfDays,
                              impressionGoal,
                            };
                          })}
                          columns={[
                            {
                              title: <span>Start Date <Text type="danger">*</Text></span>,
                              dataIndex: 'startDate',
                              key: 'startDate',
                              width: 150,
                              render: (value, _, idx) => (
                                <DatePicker
                                  style={{
                                    width: '100%',
                                    borderColor: !value ? '#ff4d4f' : undefined
                                  }}
                                  value={value ? moment(value).tz(timezone) : null}
                                  format="MM/DD/YYYY"
                                  placeholder="Start date"
                                  status={!value ? 'error' : undefined}
                                  disabled={isLaunching || isSavingCampaign}
                                  onChange={(date) => {
                                    const updated = [...flightSchedule];
                                    const normalizedDate = date ? moment.tz(date.format('YYYY-MM-DD'), timezone).startOf('day').utc() : null;
                                    updated[idx] = { ...updated[idx], startDate: normalizedDate };
                                    setFlightSchedule(updated);
                                  }}
                                />
                              ),
                            },
                            {
                              title: <span>End Date <Text type="danger">*</Text></span>,
                              dataIndex: 'endDate',
                              key: 'endDate',
                              width: 150,
                              render: (value, _, idx) => (
                                <DatePicker
                                  style={{
                                    width: '100%',
                                    borderColor: !value ? '#ff4d4f' : undefined
                                  }}
                                  value={value ? moment(value).tz(timezone) : null}
                                  format="MM/DD/YYYY"
                                  placeholder="End date"
                                  status={!value ? 'error' : undefined}
                                  disabled={isLaunching || isSavingCampaign}
                                  onChange={(date) => {
                                    const updated = [...flightSchedule];
                                    const normalizedDate = date ? moment.tz(date.format('YYYY-MM-DD'), timezone).endOf('day').utc() : null;
                                    updated[idx] = { ...updated[idx], endDate: normalizedDate };
                                    setFlightSchedule(updated);
                                    autoAdjustEndDatesFromFlightSchedule(updated);
                                  }}
                                />
                              ),
                            },
                            {
                              title: <span>Budget <Text type="danger">*</Text></span>,
                              dataIndex: 'budget',
                              key: 'budget',
                              width: 150,
                              render: (value, _, idx) => (
                                <InputNumber
                                  style={{
                                    width: '100%',
                                    borderColor: (!value || value <= 0) ? '#ff4d4f' : undefined
                                  }}
                                  value={value}
                                  placeholder="500"
                                  prefix="$"
                                  min={0}
                                  precision={2}
                                  status={(!value || value <= 0) ? 'error' : undefined}
                                  disabled={isLaunching || isSavingCampaign}
                                  formatter={val => val ? `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                                  parser={val => val.replace(/\$\s?|(,*)/g, '')}
                                  onChange={(newValue) => {
                                    const updated = [...flightSchedule];
                                    updated[idx] = { ...updated[idx], budget: newValue };
                                    setFlightSchedule(updated);
                                  }}
                                />
                              ),
                            },
                            // CPM column - hidden for Percentage pricing
                            ...(pricingModel?.name !== 'Percentage' ? [{
                              title: <span>CPM <Text type="danger">*</Text></span>,
                              dataIndex: 'cpm',
                              key: 'cpm',
                              width: 120,
                              render: (value, _, idx) => (
                                <InputNumber
                                  style={{
                                    width: '100%',
                                    borderColor: (!value || value < 1) ? '#ff4d4f' : undefined
                                  }}
                                  value={value}
                                  placeholder="10"
                                  min={0}
                                  precision={2}
                                  status={(!value || value < 1) ? 'error' : undefined}
                                  disabled={isLaunching || isSavingCampaign}
                                  onChange={(newValue) => {
                                    const updated = [...flightSchedule];
                                    updated[idx] = { ...updated[idx], cpm: newValue };
                                    setFlightSchedule(updated);
                                  }}
                                />
                              ),
                            }] : []),
                            // Impressions column - hidden for Percentage pricing
                            ...(pricingModel?.name !== 'Percentage' ? [{
                              title: 'Impressions',
                              dataIndex: 'impressionGoal',
                              key: 'impressionGoal',
                              width: 120,
                              align: 'right',
                              render: (value) => (
                                <Text>{value ? value.toLocaleString() : '-'}</Text>
                              ),
                            }] : []),
                            {
                              title: 'Days',
                              dataIndex: 'numberOfDays',
                              key: 'numberOfDays',
                              width: 80,
                              align: 'center',
                              render: (value) => (
                                <Text>{value !== null ? value : '-'}</Text>
                              ),
                            },
                            {
                              title: '',
                              key: 'actions',
                              width: 50,
                              align: 'center',
                              render: (_, __, idx) => (
                                <Button
                                  danger
                                  size="small"
                                  type="text"
                                  icon={<DeleteOutlined />}
                                  disabled={isLaunching || isSavingCampaign}
                                  onClick={() => {
                                    const updated = flightSchedule.filter((_, i) => i !== idx);
                                    setFlightSchedule(updated);
                                  }}
                                />
                              ),
                            },
                          ]}
                          pagination={false}
                          size="small"
                          locale={{
                            emptyText: 'No campaign flight dates configured. Click "Add Flight" to add a new flight.',
                          }}
                        />

                        {/* Save Campaign Button - Bottom Right */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                          <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={isSavingCampaign}
                            disabled={isLaunching}
                            onClick={handleSaveCampaign}
                          >
                            Save Campaign
                          </Button>
                        </div>
                      </Space>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Campaign-Level Flight Schedules (read-only for non-advanced mode) */}
              {!isAdvancedMode && (() => {
                const flightSchedules = currentCampaignOrder.campaigns[campaignIndex]?.flightSchedules || [];
                if (flightSchedules.length === 0) return null;

                const isPercentage = pricingModel?.name === 'Percentage';
                const dataSource = flightSchedules.map((flight, idx) => {
                  const numberOfDays = flight.startDate && flight.endDate
                    ? moment(flight.endDate).diff(moment(flight.startDate), 'days') + 1
                    : null;
                  const impressionGoal = flight.budget && flight.cpm
                    ? Math.round((flight.budget / flight.cpm) * 1000)
                    : null;
                  return { key: idx, ...flight, numberOfDays, impressionGoal };
                });

                const columns = [
                  {
                    title: 'Flight',
                    key: 'flight',
                    width: 60,
                    align: 'center',
                    render: (_, __, idx) => <Text>{idx + 1}</Text>,
                  },
                  {
                    title: 'Start Date',
                    dataIndex: 'startDate',
                    key: 'startDate',
                    width: 120,
                    render: (value) => <Text>{value ? moment.utc(value).format('MM/DD/YYYY') : '-'}</Text>,
                  },
                  {
                    title: 'End Date',
                    dataIndex: 'endDate',
                    key: 'endDate',
                    width: 120,
                    render: (value) => <Text>{value ? moment.utc(value).format('MM/DD/YYYY') : '-'}</Text>,
                  },
                  {
                    title: 'Budget',
                    dataIndex: 'budget',
                    key: 'budget',
                    width: 120,
                    align: 'right',
                    render: (value) => <Text>{value ? formatCurrency2SigFig(value) : '-'}</Text>,
                  },
                  ...(!isPercentage ? [{
                    title: 'CPM',
                    dataIndex: 'cpm',
                    key: 'cpm',
                    width: 100,
                    align: 'right',
                    render: (value) => <Text>{value ? formatCurrency2SigFig(value) : '-'}</Text>,
                  }] : []),
                  ...(!isPercentage ? [{
                    title: 'Impressions',
                    dataIndex: 'impressionGoal',
                    key: 'impressionGoal',
                    width: 120,
                    align: 'right',
                    render: (value) => <Text>{value ? value.toLocaleString() : '-'}</Text>,
                  }] : []),
                  {
                    title: 'Days',
                    dataIndex: 'numberOfDays',
                    key: 'numberOfDays',
                    width: 70,
                    align: 'center',
                    render: (value) => <Text>{value !== null ? value : '-'}</Text>,
                  },
                ];

                return (
                  <Row gutter={20} style={{ marginTop: 16 }}>
                    <Col span={24}>
                      <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>Flight Schedules</Text>
                      <Table
                        dataSource={dataSource}
                        columns={columns}
                        pagination={false}
                        size="small"
                        bordered
                      />
                    </Col>
                  </Row>
                );
              })()}

               {/* Show Delivery Items Only Toggle - Advanced Mode Only */}
               {isAdvancedMode && (
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={24}>
                    <Space align="center">
                      <Switch
                        checked={showOnlyDeliveryItems}
                        onChange={setShowOnlyDeliveryItems}
                      />
                      <Text strong>Show Delivery Items Only</Text>
                      <Tooltip title="Filter to display only line items marked for delivery">
                        <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                      </Tooltip>
                    </Space>
                  </Col>
                </Row>
              )}

              {/* Campaign Launch Progress Section - Only show for programmatic launches */}
              {isLaunching && currentCampaignOrder.campaigns[campaignIndex]?.isLaunchProgrammatically && (
                <>
                  <Divider plain style={{ margin: '16px 0' }} />
                  <Alert
                    message={
                      <Space>
                        <RocketOutlined style={{ fontSize: 16 }} />
                        <Text strong>
                          {isUpdateMode
                            ? 'Campaign Update in Progress'
                            : 'Campaign Launch in Progress'}
                        </Text>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 12 }}>
                        {/* Progress Bar */}
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                            <Text strong style={{ marginRight: 8 }}>Progress:</Text>
                            <Text type="secondary">{progressData.progress || 0}%</Text>
                          </div>
                          <Progress
                            percent={progressData.progress || 0}
                            size="default"
                            status={
                              progressData.isFailed
                                ? 'exception'
                                : progressData.isCompleted
                                ? 'success'
                                : 'active'
                            }
                            strokeWidth={10}
                          />
                        </div>

                        {/* Current Phase and Message */}
                        <div>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            {progressData.phase && (
                              <div>
                                <Text strong>Phase: </Text>
                                <Tag color="blue">{progressData.phase}</Tag>
                              </div>
                            )}
                            <div>
                              <Text strong>Status: </Text>
                              <Text type="secondary">
                                {progressData.message ||
                                  (isUpdateMode ? 'Initializing line item addition...' : 'Initializing campaign launch...')}
                              </Text>
                            </div>
                          </Space>
                        </div>

                        {/* Elapsed Time */}
                        {progressData.elapsedTimeFormatted && (
                          <div>
                            <Space>
                              <ClockCircleOutlined style={{ color: '#1890ff' }} />
                              <Text strong>Elapsed Time: </Text>
                              <Text style={{ color: '#1890ff' }}>
                                {progressData.elapsedTimeFormatted}
                              </Text>
                            </Space>
                          </div>
                        )}

                        {/* Error Details */}
                        {progressData.isFailed && progressData.progressData?.metadata?.errorDetails && (
                          <Alert
                            message="Launch Failed"
                            description={progressData.progressData.metadata.errorDetails}
                            type="error"
                            showIcon
                            style={{ marginTop: 8 }}
                          />
                        )}

                        {/* Cancel/Reset Button */}
                        <div style={{ marginTop: 8 }}>
                          <Button
                            danger
                            size="small"
                            loading={isCancellingLaunch}
                            onClick={handleCancelLaunch}
                            icon={<CloseCircleOutlined />}
                          >
                            Cancel Launch
                          </Button>
                          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                            Resets status to allow re-launching
                          </Text>
                        </div>
                      </Space>
                    }
                    type={
                      progressData.isFailed
                        ? 'error'
                        : progressData.isCompleted
                        ? 'success'
                        : 'info'
                    }
                    showIcon
                    icon={
                      progressData.isFailed ? null : progressData.isCompleted ? null : <LoadingOutlined />
                    }
                  />
                </>
              )}

              {/* Launch Summary Panel - Shows results after programmatic launch completes */}
              {showLaunchSummary && (
                <LaunchSummaryPanel
                  launchSummary={launchSummary}
                  campaignId={campaignId}
                />
              )}

              <Divider plain></Divider>

              {/* Approve All Creatives Button */}
              {isReviewMode && pendingCreativesInfo.totalPending > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Tooltip title={`Approve all ${pendingCreativesInfo.totalPending} pending creative(s) in this campaign`}>
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                      onClick={handleApproveAllCreatives}
                      loading={loadingApproveAll}
                    >
                      Approve All Creatives ({pendingCreativesInfo.totalPending})
                    </Button>
                  </Tooltip>
                </div>
              )}

              <Form.List name={[fieldName, 'lineItems']}>
                {(fields) => {
                  // Filter line items based on delivery status if toggle is enabled
                  const filteredFields = showOnlyDeliveryItems
                    ? fields.filter((field) => {
                        // Get line item from currentCampaignOrder data
                        const lineItem = currentCampaignOrder.campaigns[campaignIndex]?.lineItems?.[field.name];
                        return lineItem && lineItem.markedForDelivery === true;
                      })
                    : fields;

                  return (
                    <>
                      {filteredFields.map(
                        ({ key, name: fieldNameLineItems, fieldKey, ...restField }) => (
                          <>
                            <CampaignOrderQueueReviewCampaign
                              key={key}
                              form={form}
                              restField={restField}
                              fieldName={fieldNameLineItems}
                              fieldKey={fieldKey}
                              lineItemIndex={fieldNameLineItems}
                              campaignIndex={campaignIndex}
                              onMediaStatusUpdate={onMediaStatusUpdate}
                              isLaunching={isLaunching}
                              isAdvancedMode={isAdvancedMode}
                              campaignFlightSchedules={flightSchedule}
                              campaignOrderData={campaignOrderData}
                            />
                            <Divider />
                          </>
                        )
                      )}
                    </>
                  );
                }}
              </Form.List>

              <DateValidationError
                dateValidationErrors={dateValidationErrors}
                isAutoAdjusting={isAutoAdjustingDates}
                onAutoAdjust={handleAutoAdjustDates}
                onOpenRequester={() => {
                  const campaignOrderId = currentCampaignOrder.id;
                  history.push(`/campaigns/requester/edit/${campaignOrderId}`);
                }}
                onDismiss={() => setDateValidationErrors([])}
              />

                {(() => {
                  const condition1 = !['LIVE', 'PAUSED'].includes(currentCampaignOrder?.campaigns?.[campaignIndex]?.status);
                  const condition2 = ['LIVE_APPROVED', 'PAUSED_APPROVED', 'PAUSED_PENDING', 'LIVE_PENDING'].includes(currentCampaignOrder?.status);
                  const condition3 = !['PENDING', 'LIVE_PENDING', 'PAUSED_PENDING'].includes(currentCampaignOrder?.status);
                  const condition4 = !isLaunching;
                  // Allow button section if in update mode OR if launch conditions are met
                  const finalCondition = isProgrammaticUpdateMode || ((condition1 || condition2) && condition3 && condition4);

                  return finalCondition;
                })() && (
                  <>
                  {!currentCampaignOrder?.awarenessCampaign &&
                    !(currentCampaignOrder.transactionsConfig?.tag || currentCampaignOrder.signUpConfig?.appId || currentCampaignOrder.sessionConfig?.appId) && (
                    <Alert
                      message="Tracking tag required to launch"
                      description={
                        <span>
                          This campaign needs at least one tracking tag (transaction, sign-up, or session) before it can be launched.
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, marginLeft: 4 }}
                            loading={loadingUpdateCampaignOrder}
                            onClick={async () => {
                              try {
                                await safeUpdateCampaignOrder({
                                  variables: {
                                    where: { id: currentCampaignOrder.id },
                                    data: {
                                      awarenessCampaign: true,
                                      transactionsConfig: null,
                                      signUpConfig: null,
                                    },
                                  },
                                });
                                message.success('Campaign updated to Awareness only');
                              } catch {
                                message.error('Failed to update campaign');
                              }
                            }}
                          >
                            Mark as Awareness only campaign
                          </Button>
                        </span>
                      }
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                    />
                  )}
                    {demandSidePlatform === 'NEXXEN' ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-end',
                          gap: 12,
                          width: '100%',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Button
                          style={{ width: 140 }}
                          type="default"
                          disabled={loadingUpdateCampaignOrder}
                          onClick={() => handleLaunchManual()}
                        >
                          {isCampaignLaunched ? 'Update Manual' : 'Launch Manual'}
                        </Button>
                        {isProgrammaticUpdateMode ? (
                          // UPDATE MODE: Single button, no dropdown
                          <Tooltip
                            title={
                              isAnotherProgrammaticCampaignLaunching
                                ? `Please wait: "${otherProgrammaticLaunchingCampaign?.name || 'Another campaign'}" is currently launching`
                                : ''
                            }
                          >
                            <Button
                              style={{ width: 200 }}
                              type="primary"
                              disabled={
                                loadingUpdateCampaignOrder ||
                                loadingLaunchCampaingOrderProgrammatic ||
                                isAnotherProgrammaticCampaignLaunching
                              }
                              onClick={handleUpdateProgrammatic}
                            >
                              Update Programmatically
                            </Button>
                          </Tooltip>
                        ) : (
                          // LAUNCH MODE: Dropdown with play/pause options
                          <Tooltip
                            title={
                              isAnotherProgrammaticCampaignLaunching
                                ? `Please wait: "${otherProgrammaticLaunchingCampaign?.name || 'Another campaign'}" is currently launching`
                                : ''
                            }
                          >
                            <span style={{ display: 'inline-block' }}>
                              <Dropdown
                                trigger={['click']}
                                disabled={!hasNewItemsToLaunch || isAnotherProgrammaticCampaignLaunching || loadingUpdateCampaignOrder || loadingLaunchCampaingOrderProgrammatic}
                                menu={{
                                  items: [
                                    {
                                      key: 'play',
                                      label: isCampaignLaunched ? 'Update Auto Play' : 'Launch Auto Play',
                                      onClick: () => handleLaunchProgrammatic('play')
                                    },
                                    {
                                      key: 'pause',
                                      label: isCampaignLaunched ? 'Update Pause' : 'Launch Pause',
                                      onClick: () => handleLaunchProgrammatic('pause')
                                    }
                                  ]
                                }}
                              >
                                <Button
                                  style={{ width: 200 }}
                                  type="primary"
                                  disabled={
                                    !hasNewItemsToLaunch || loadingUpdateCampaignOrder || loadingLaunchCampaingOrderProgrammatic || isAnotherProgrammaticCampaignLaunching
                                  }
                                >
                                  {isCampaignLaunched ? 'Update Programmatically' : 'Launch Programmatically'}
                                </Button>
                              </Dropdown>
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                        }}
                      >
                        <Button
                          style={{ width: 100 }}
                          type="primary"
                          disabled={loadingUpdateCampaignOrder}
                          onClick={() => handleLaunchManual()}
                        >
                          {isCampaignLaunched ? 'Update' : 'Launch'}
                        </Button>
                      </div>
                    )}
                  </>
                )}
            </Space>
          </Card>
          </Panel>
        </Collapse>
      </div>

      <CampaignLaunchProgressModal
        visible={showProgressModal}
        campaignOrderId={currentCampaignOrder?.id}
        campaignName={currentCampaignOrder?.name || 'Campaign'}
        isUpdateMode={isUpdateMode}
        newLineItemCount={newLineItemCount}
        onCancel={() => setShowProgressModal(false)}
        onCompleted={() => {
          setShowProgressModal(false);
          if (isUpdateMode) {
            message.success(`Successfully added ${newLineItemCount} new line item${newLineItemCount !== 1 ? 's' : ''} to campaign!`);
          } else {
            message.success('Campaign launched successfully!');
          }
          // Navigate based on the launch status
          if (currentLaunchStatus === 'pause') {
            history.push('/campaigns/v2/orderqueue/paused');
          } else {
            history.push('/campaigns/v2/orderqueue/launched');
          }
        }}
        onFailed={(progressData) => {
          setShowProgressModal(false);
          const errorMsg = progressData?.metadata?.errorDetails ||
            (isUpdateMode ? 'Failed to add line items' : 'Campaign launch failed');
          message.error(errorMsg);
          // Failed campaigns might remain in launching tab with error status
        }}
        onError={(error) => {
          console.error('Progress subscription error:', error);
          // Keep modal open but show connection error within modal
        }}
      />

      {/* Add/Update Line Items Confirmation Modal */}
      <Modal
        title={
          newLineItemCount > 0 && modifiedLineItemCount > 0
            ? 'Confirm Adding & Updating Line Items'
            : newLineItemCount > 0
            ? 'Confirm Adding Line Items'
            : modifiedLineItemCount > 0
            ? 'Confirm Updating Line Items'
            : 'Confirm Campaign Update'
        }
        open={showAddLineItemsModal}
        onOk={handleConfirmAddLineItems}
        onCancel={() => setShowAddLineItemsModal(false)}
        okText="Confirm & Update"
        okButtonProps={{ type: 'primary' }}
        cancelText="Cancel"
        width={600}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            message="Update Programmatically"
            description={
              newLineItemCount > 0 && modifiedLineItemCount > 0
                ? `This campaign already has an Ad Provider ID. You are about to add ${newLineItemCount} new line item${newLineItemCount !== 1 ? 's' : ''} and update ${modifiedLineItemCount} existing line item${modifiedLineItemCount !== 1 ? 's' : ''} programmatically.`
                : newLineItemCount > 0
                ? `This campaign already has an Ad Provider ID. You are about to add ${newLineItemCount} new line item${newLineItemCount !== 1 ? 's' : ''} programmatically to the existing campaign.`
                : modifiedLineItemCount > 0
                ? `You are about to update ${modifiedLineItemCount} existing line item${modifiedLineItemCount !== 1 ? 's' : ''} programmatically.`
                : `You are about to sync campaign settings (budget, dates, bid) to Nexxen DSP.`
            }
            type="info"
            showIcon
          />

          {newLineItemCount > 0 && (
            <div>
              <Text strong>New Line Items to be Added:</Text>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {newLineItems.map((lineItem, index) => (
                  <li key={lineItem.id || index}>
                    <Text>{lineItem.name || `Line Item ${index + 1}`}</Text>
                    {lineItem.budgetTotal && (
                      <Text type="secondary"> - ${lineItem.budgetTotal}</Text>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {modifiedLineItemCount > 0 && (
            <div>
              <Text strong>Existing Line Items to be Updated:</Text>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {modifiedLineItems.map((lineItem, index) => (
                  <li key={lineItem.id || index}>
                    <Text>{lineItem.name || `Line Item ${index + 1}`}</Text>
                    {lineItem.budgetTotal && (
                      <Text type="secondary"> - ${lineItem.budgetTotal}</Text>
                    )}
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      ({lineItem.status === 'PLAY_PENDING' ? 'Will play' : 'Will pause'})
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {newCreativesInfo.count > 0 && (
            <div>
              <Text strong style={{ color: '#1890ff' }}>
                New Creatives to be Added: {newCreativesInfo.total} creative{newCreativesInfo.total > 1 ? 's' : ''} in {newCreativesInfo.count} line item{newCreativesInfo.count > 1 ? 's' : ''}
              </Text>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {newCreativesInfo.items.map((lineItem) => (
                  <li key={lineItem.id}>
                    <Text>{lineItem.name}</Text>
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      (+{lineItem.newMediaCount} new)
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {newLineItemCount === 0 && modifiedLineItemCount === 0 && newCreativesInfo.count === 0 && (
            <div>
              <Text strong>Campaign Settings to be Synced:</Text>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li><Text>Budget & Flight Dates</Text></li>
                <li><Text>Max Bid (CPM)</Text></li>
              </ul>
            </div>
          )}

          {newLineItemCount > 0 && (
            <Alert
              message="Launch Status"
              description={`New line items will be launched with status: ${pendingLaunchStatus === 'play' ? 'LIVE' : 'PAUSED'}`}
              type="warning"
              showIcon
            />
          )}
        </Space>
      </Modal>
    </Spin>
  );
};
